import ToolDefinition from "@shared/GenericTools/ToolDefinition.js";
import {isToolAvailable} from "@shared/GenericTools/ToolReleaseStatus.js";
import {LongitudinalClassId, LongitudinalRendererId} from "@shared/GenericTools/longitudinal/LongitudinalClassEnum.js";
import {createFeatureMatchingAssessmentConfig, createFeatureMatchingAssessmentDemoPayload} from "@shared/GenericTools/instruments/FeatureMatchingAssessment.js";
import {createSchemaTherapyCaseConceptualizationConfig, createSchemaTherapyCaseConceptualizationDemoPayload} from "@shared/GenericTools/instruments/SchemaTherapyCaseConceptualizationForm.js";
import {createEftCaseFormulationConfig, createEftCaseFormulationDemoPayload} from "@shared/GenericTools/instruments/EftCaseFormulation.js";
import {ToolEngineEnum} from "@shared/GenericTools/ToolEngineEnum.js";
import {ToolParticipationModeEnum} from "@shared/GenericTools/ToolParticipationModeEnum.js";
import {ToolRendererEnum} from "@shared/GenericTools/ToolRendererEnum.js";
import {ToolResultBehaviorEnum} from "@shared/GenericTools/ToolResultBehaviorEnum.js";
import {ToolSurfaceEnum} from "@shared/GenericTools/ToolSurfaceEnum.js";
import {ToolTypeEnum} from "@shared/GenericTools/ToolTypeEnum.js";
import {createCyBocsConfig, createCyBocsDemoPayload} from "@shared/GenericTools/instruments/CyBocsFifthRevision.js";
import {createCyBocsSecondEditionConfig, createCyBocsSecondEditionDemoPayload} from "@shared/GenericTools/instruments/CyBocsSecondEdition.js";
import {createCoisRConfig, createCoisRDemoPayload} from "@shared/GenericTools/instruments/CoisR.js";
import {createSchemaModeInventoryConfig, createSchemaModeInventoryDemoConfig, createSchemaModeInventoryDemoPayload} from "@shared/GenericTools/instruments/SchemaModeInventory.js";
import {createYoungSchemaQuestionnaireL3Config, createYoungSchemaQuestionnaireL3DemoPayload} from "@shared/GenericTools/instruments/YoungSchemaQuestionnaireL3.js";
import {createOciCvConfig, createOciCvDemoPayload} from "@shared/GenericTools/instruments/OciCv.js";
import {createCareerConstructionInterviewConfig, createCareerConstructionInterviewDemoPayload} from "@shared/GenericTools/instruments/CareerConstructionInterview.js";
import {createImpactStatementConfig, createImpactStatementDemoPayload} from "@shared/GenericTools/instruments/ImpactStatement.js";
import {createSelfMonitoringConfig, createSelfMonitoringDemoConfig, createSelfMonitoringDemoPayload} from "@shared/GenericTools/instruments/SelfMonitoringForm.js";
import {createMomentaryTimeSamplingConfig, createMomentaryTimeSamplingDemoConfig, createMomentaryTimeSamplingDemoPayload} from "@shared/GenericTools/instruments/MomentaryTimeSampling.js";
import {createSudsConfig, createSudsDemoPayload} from "@shared/GenericTools/instruments/SubjectiveUnitsOfDistressScale.js";
import {createMohostConfig, createMohostDemoPayload, createMohostPriorResults} from "@shared/GenericTools/instruments/Mohost.js";
import {createOcairsConfig, createOcairsDemoPayload} from "@shared/GenericTools/instruments/Ocairs.js";
import {createHappinessScaleConfig, createHappinessScaleDemoPayload, createHappinessScaleDemoPriorResults} from "@shared/GenericTools/instruments/HappinessScale.js";
import {createInterpersonalInventoryConfig, createInterpersonalInventoryDemoPayload} from "@shared/GenericTools/instruments/InterpersonalInventory.js";
import {createPresentTriggerWorksheetConfig} from "@shared/GenericTools/instruments/PresentTriggerWorksheet.js";
import {createPatternsOfProblematicThinkingConfig, createPatternsOfProblematicThinkingDemoPayload} from "@shared/GenericTools/instruments/PatternsOfProblematicThinking.js";
import {createStatementOfPositionMapConfig} from "@shared/GenericTools/instruments/StatementOfPositionMap.js";
import {createDeconstructionQuestionsConfig, createDeconstructionQuestionsDemoPayload} from "@shared/GenericTools/instruments/DeconstructionQuestions.js";
import {createImaginalExposureScriptConfig, createImaginalExposureScriptDemoPayload} from "@shared/GenericTools/instruments/ImaginalExposureScriptWorksheet.js";
import {createImportanceRulerConfig, createImportanceRulerDemoPayload} from "@shared/GenericTools/instruments/ImportanceRuler.js";
import {createWallQuestionConfig, createWallQuestionDemoPayload, createWallQuestionDemoPriorResults} from "@shared/GenericTools/instruments/WallQuestion.js";
import {createPatientExRpAdherenceScaleConfig, createPatientExRpAdherenceScaleDemoPayload, createPatientExRpAdherenceScaleDemoPriorResults} from "@shared/GenericTools/instruments/PatientExRpAdherenceScale.js";
import {createAREQuestionnaireConfig, createAREQuestionnaireDemoPayload} from "@shared/GenericTools/instruments/AREQuestionnaire.js";
import {createPatientRulesForRitualPreventionConfig, createPatientRulesForRitualPreventionDemoConfig, createPatientRulesForRitualPreventionDemoPayload} from "@shared/GenericTools/instruments/PatientRulesForRitualPrevention.js";

const sharedFormSurfaces = [
	ToolSurfaceEnum.PUBLIC_DEMO,
	ToolSurfaceEnum.PRACTITIONER_TEMPLATE,
	ToolSurfaceEnum.CLIENT_WORK,
	ToolSurfaceEnum.RESULT_REVIEW,
]

const patternsOfProblematicThinkingDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.PATTERNS_OF_PROBLEMATIC_THINKING_WORKSHEET,
	slug: "patterns-of-problematic-thinking-worksheet",
	title: "Patterns of Problematic Thinking Worksheet",
	shortDescription: "Collects thoughts and stuck points, then reviews recurring thinking patterns.",
	displaySummary: "Use a Cognitive Processing Therapy worksheet to group distinct thoughts by thinking pattern and review supporting examples.",
	interactionModel: "Clients record thoughts and may suggest categories; practitioners review placements and choose a focus.",
	engine: ToolEngineEnum.STAGED_WORKSHEET,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.PATTERN_PROFILE_CONFIG,
	workRenderer: ToolRendererEnum.PATTERN_PROFILE_WORK,
	resultRenderer: ToolRendererEnum.PATTERN_PROFILE_RESULT,
	blankPrintRenderer: ToolRendererEnum.PATTERN_PROFILE_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.PATTERN_PROFILE_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	searchKeywords: ["CPT", "patterns of problematic thinking", "stuck points", "cognitive patterns"],
	defaultConfig: createPatternsOfProblematicThinkingConfig(),
	publicDemoConfig: {demoEnginePayload: createPatternsOfProblematicThinkingDemoPayload()},
})

function createTitleElement(title, description = "") {
	return {
		id: `${titleElementIdFromTitle(title)}Title`,
		type: "TITLE",
		title,
		description,
		showDescription: !!description,
	}
}

function titleElementIdFromTitle(title = "") {
	return title.replace(/[^a-z0-9]+/gi, " ").trim().split(" ").map((part, index) => {
		let result = part.toLowerCase()
		if (index > 0) {
			result = `${result.charAt(0).toUpperCase()}${result.slice(1)}`
		}
		return result
	}).join("") || "form"
}

function createTextElement(id, title, isRequired = false) {
	return {
		id,
		type: "TEXT",
		title,
		isRequired,
	}
}

function createMultilineElement(id, title, isRequired = false) {
	return {
		id,
		type: "MULTILINE",
		title,
		isRequired,
	}
}

function createDateElement(id, title, isRequired = false) {
	return {
		id,
		type: "DATE",
		title,
		isRequired,
	}
}

function createRadioElement(id, title, answerOptions = [], isRequired = false) {
	return {
		id,
		type: "RADIOBUTTONS",
		title,
		answerOptions,
		answerOptionsScoring: answerOptions.map(() => 0),
		isRequired,
		enableScoring: false,
		showScoring: false,
	}
}

function createCheckboxesElement(id, title, answerOptions = [], isRequired = false) {
	return {
		id,
		type: "CHECKBOXES",
		title,
		answerOptions,
		answerOptionsScoring: answerOptions.map(() => 0),
		isRequired,
		enableScoring: false,
		showScoring: false,
	}
}

function createScaleElement(id, title, lowLabel, highLabel, isRequired = true, max = 10) {
	return {
		id,
		type: "SCALE",
		title,
		isRequired,
		useCustomRange: false,
		max,
		lowLabel,
		highLabel,
		answer: null,
	}
}

function createProfileArea(id, title, practitionerNote = "") {
	return {
		id,
		title,
		practitionerNote,
		rating: null,
		clientNote: "",
	}
}

function createItemListItem(id, title, description = "") {
	return {
		id,
		title,
		description,
	}
}

function createDiaryConfigItem(id, title, responseType = "count", description = "") {
	return {
		id,
		title,
		responseType,
		description,
	}
}

function createDiaryRatingSection(id, title, items = []) {
	return {
		id,
		title,
		items,
	}
}

function createDiaryEntry(dayId, dayLabel, options = {}) {
	return {
		dayId,
		dayLabel,
		isComplete: !!options.isComplete,
		answers: {
			ratings: options.ratings || {},
			targetBehaviors: options.targetBehaviors || {},
			contextFields: options.contextFields || {},
			skills: options.skills || {},
			notes: options.notes || "",
			sessionEntryRatings: options.sessionEntryRatings || {},
		},
	}
}

function createStagedEntryField(id, title, responseType = "text", options = {}) {
	return {
		id,
		title,
		responseType,
		placeholder: options.placeholder || "",
		options: options.options || [],
		isRequired: !!options.isRequired,
	}
}

function createChoiceOptions(labels = []) {
	return labels.map(option => ({
		label: option.label || option,
		value: option.value || String(option.label || option).replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "").toLowerCase(),
	}))
}

const defaultValuesCardSortItems = [
	createItemListItem("acceptance", "ACCEPTANCE", "to make room for reality, feelings, and people as they are"),
	createItemListItem("accountability", "ACCOUNTABILITY", "to take ownership of my choices and their impact"),
	createItemListItem("achievement", "ACHIEVEMENT", "to accomplish meaningful goals and make progress"),
	createItemListItem("adventure", "ADVENTURE", "to seek experiences that feel new, bold, or exciting"),
	createItemListItem("art", "ART", "to appreciate, create, or express through art, music, writing, or design"),
	createItemListItem("authenticity", "AUTHENTICITY", "to live and speak in ways that feel true to who I am"),
	createItemListItem("autonomy", "AUTONOMY", "to have freedom, choice, and self-direction"),
	createItemListItem("balance", "BALANCE", "to make space for different parts of life without one taking over"),
	createItemListItem("beauty", "BEAUTY", "to notice and appreciate beauty in people, places, and experiences"),
	createItemListItem("belonging", "BELONGING", "to feel accepted, included, and part of something"),
	createItemListItem("calm", "CALM", "to create steadiness, quiet, and emotional ease"),
	createItemListItem("challenge", "CHALLENGE", "to take on difficult tasks that stretch me"),
	createItemListItem("comfort", "COMFORT", "to build a life that feels pleasant, easeful, and supportive"),
	createItemListItem("commitment", "COMMITMENT", "to stay devoted to people, choices, or paths that matter"),
	createItemListItem("compassion", "COMPASSION", "to respond to suffering with care and concern"),
	createItemListItem("community", "COMMUNITY", "to contribute to and feel connected with a wider group"),
	createItemListItem("contribution", "CONTRIBUTION", "to make a positive difference beyond myself"),
	createItemListItem("cooperation", "COOPERATION", "to work with others toward shared goals"),
	createItemListItem("courage", "COURAGE", "to act with bravery when something is difficult or uncertain"),
	createItemListItem("creativity", "CREATIVITY", "to make, imagine, build, or solve things in original ways"),
	createItemListItem("curiosity", "CURIOSITY", "to stay interested, ask questions, and keep exploring"),
	createItemListItem("dependability", "DEPENDABILITY", "to be reliable and follow through on what I say"),
	createItemListItem("diligence", "DILIGENCE", "to do careful, steady, and conscientious work"),
	createItemListItem("empathy", "EMPATHY", "to understand and care about another person's experience"),
	createItemListItem("environment", "ENVIRONMENT", "to live with care for nature and the world around me"),
	createItemListItem("equality", "EQUALITY", "to support equal worth, dignity, and opportunity for people"),
	createItemListItem("excellence", "EXCELLENCE", "to do things with quality, care, and high standards"),
	createItemListItem("excitement", "EXCITEMENT", "to include energy, stimulation, and enthusiasm in life"),
	createItemListItem("faith", "FAITH", "to trust in something larger, sacred, or deeply held"),
	createItemListItem("family", "FAMILY", "to invest in close family bonds and responsibilities"),
	createItemListItem("fitness", "FITNESS", "to keep my body strong, capable, and active"),
	createItemListItem("flexibility", "FLEXIBILITY", "to adapt when life changes or plans shift"),
	createItemListItem("forgiveness", "FORGIVENESS", "to release resentment and move toward repair or peace"),
	createItemListItem("freedom", "FREEDOM", "to live with independence and room to choose my path"),
	createItemListItem("friendship", "FRIENDSHIP", "to build caring, enjoyable, and supportive friendships"),
	createItemListItem("fun", "FUN", "to play, laugh, and enjoy life"),
	createItemListItem("generosity", "GENEROSITY", "to share time, attention, resources, or care with others"),
	createItemListItem("gratitude", "GRATITUDE", "to notice and appreciate what is good or meaningful"),
	createItemListItem("growth", "GROWTH", "to keep learning, changing, and developing"),
	createItemListItem("health", "HEALTH", "to care for my physical, mental, and emotional wellbeing"),
	createItemListItem("honesty", "HONESTY", "to be truthful with myself and others"),
	createItemListItem("hope", "HOPE", "to stay connected to possibility and a better future"),
	createItemListItem("humility", "HUMILITY", "to stay grounded, open, and aware of my limits"),
	createItemListItem("humor", "HUMOR", "to notice lightness, playfulness, and the funny side of life"),
	createItemListItem("independence", "INDEPENDENCE", "to rely on myself and make my own choices"),
	createItemListItem("integrity", "INTEGRITY", "to act in line with my principles even when it is hard"),
	createItemListItem("intimacy", "INTIMACY", "to share closeness, vulnerability, and emotional connection"),
	createItemListItem("justice", "JUSTICE", "to promote fairness and challenge harm or oppression"),
	createItemListItem("kindness", "KINDNESS", "to treat myself and others with warmth and care"),
	createItemListItem("knowledge", "KNOWLEDGE", "to learn, understand, and use insight wisely"),
	createItemListItem("leadership", "LEADERSHIP", "to guide, influence, or support others toward a shared direction"),
	createItemListItem("leisure", "LEISURE", "to make time for rest, relaxation, and enjoyment"),
	createItemListItem("love", "LOVE", "to give and receive deep care, affection, and connection"),
	createItemListItem("loyalty", "LOYALTY", "to stand by people, commitments, or principles I value"),
	createItemListItem("mastery", "MASTERY", "to build skill, competence, and confidence through practice"),
	createItemListItem("meaning", "MEANING", "to live in ways that feel significant and purposeful"),
	createItemListItem("mindfulness", "MINDFULNESS", "to be present and aware in this moment"),
	createItemListItem("moderation", "MODERATION", "to avoid excess and choose enough"),
	createItemListItem("openness", "OPENNESS", "to be receptive to new ideas, feelings, and experiences"),
	createItemListItem("order", "ORDER", "to create structure, clarity, and organization"),
	createItemListItem("patience", "PATIENCE", "to stay steady when things take time"),
	createItemListItem("peace", "PEACE", "to reduce conflict and cultivate harmony"),
	createItemListItem("playfulness", "PLAYFULNESS", "to bring lightness, spontaneity, and play into life"),
	createItemListItem("practicality", "PRACTICALITY", "to focus on what is useful, realistic, and workable"),
	createItemListItem("realism", "REALISM", "to see situations clearly and respond to what is true"),
	createItemListItem("religion", "RELIGION", "to follow a specific spiritual or faith tradition"),
	createItemListItem("respect", "RESPECT", "to treat myself and others with dignity"),
	createItemListItem("responsibility", "RESPONSIBILITY", "to be dependable and meet important obligations"),
	createItemListItem("risk", "RISK", "to take chances when something meaningful is at stake"),
	createItemListItem("romance", "ROMANCE", "to nurture attraction, affection, and romantic connection"),
	createItemListItem("safety", "SAFETY", "to protect wellbeing and create security"),
	createItemListItem("selfCompassion", "SELF-COMPASSION", "to treat myself with care when I struggle"),
	createItemListItem("selfControl", "SELF-CONTROL", "to guide my actions instead of being ruled by impulses"),
	createItemListItem("selfRespect", "SELF-RESPECT", "to honor my needs, boundaries, and worth"),
	createItemListItem("service", "SERVICE", "to be helpful and useful to other people"),
	createItemListItem("sexuality", "SEXUALITY", "to express and care for my sexual self in healthy ways"),
	createItemListItem("simplicity", "SIMPLICITY", "to reduce clutter, excess, and unnecessary complication"),
	createItemListItem("solitude", "SOLITUDE", "to have meaningful time alone"),
	createItemListItem("spirituality", "SPIRITUALITY", "to connect with what feels sacred, transcendent, or deeply meaningful"),
	createItemListItem("stability", "STABILITY", "to create consistency, steadiness, and reliability"),
	createItemListItem("tolerance", "TOLERANCE", "to accept and respect differences"),
	createItemListItem("tradition", "TRADITION", "to honor practices, customs, or wisdom from the past"),
	createItemListItem("trust", "TRUST", "to build confidence, honesty, and safety in relationships"),
	createItemListItem("wealth", "WEALTH", "to build financial resources and material security"),
	createItemListItem("wisdom", "WISDOM", "to use experience and judgment to live well"),
]

const readinessRulerDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.READINESS_RULER,
	slug: "readiness-ruler",
	title: "Readiness Ruler",
	shortDescription: "Rates importance, confidence, and readiness for a client change goal.",
	displaySummary: "Use a three-part rating scale to clarify how ready a client feels to make a change.",
	engine: ToolEngineEnum.FORM,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.FORM_STANDARD_CONFIG,
	workRenderer: ToolRendererEnum.FORM_STANDARD_WORK,
	resultRenderer: ToolRendererEnum.FORM_STANDARD_RESULT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	analyticsKey: "readiness_ruler",
	searchKeywords: ["change", "motivation", "goal", "confidence", "importance", "assessment", "scale"],
	defaultConfig: {
		title: "Readiness Ruler",
		scaleConfig: {
			max: 10,
		},
		fields: [
			createTitleElement("Readiness Ruler", "Think about the change you are considering, then answer the prompts below."),
			createTextElement("changeGoal", "What change are you considering?", true),
			createScaleElement("importance", "How important is this change to you right now?", "Not important", "Very important"),
			createScaleElement("confidence", "How confident do you feel about making this change?", "Not confident", "Very confident"),
			createScaleElement("readiness", "How ready are you to start working on this change?", "Not ready", "Ready now"),
			createMultilineElement("nextStep", "What would help you move one step higher?"),
		],
	},
	publicDemoConfig: {
		title: "Readiness Ruler",
		instructions: "Choose a change goal and rate importance, confidence, and readiness.",
	},
})

const importanceRulerDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.IMPORTANCE_RULER,
	slug: "importance-ruler",
	title: "Importance Ruler",
	shortDescription: "Rates the importance of one change and captures the client's reasons.",
	displaySummary: "Ask how important a named change feels and review the client's own reasons over time.",
	interactionModel: "The client selects one importance rating and writes what the change means to them.",
	engine: ToolEngineEnum.FORM,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.FORM_RULER_CONFIG,
	workRenderer: ToolRendererEnum.FORM_RULER_WORK,
	resultRenderer: ToolRendererEnum.FORM_RULER_RESULT,
	blankPrintRenderer: ToolRendererEnum.FORM_RULER_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.FORM_RULER_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	searchKeywords: ["importance ruler", "motivational interviewing", "change talk", "motivation"],
	defaultConfig: createImportanceRulerConfig(),
	publicDemoConfig: {demoEnginePayload: createImportanceRulerDemoPayload()},
})

const wallQuestionDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.WALL_QUESTION,
	slug: "wall-question",
	title: "Wall Question",
	shortDescription: "Records a person's rating of aphasia's overall impact on life.",
	displaySummary: "Administer one repeatable life-impact rating and review descriptive change over time.",
	interactionModel: "The person with aphasia chooses one line on a nine-position pictographic scale or cannot complete the rating.",
	engine: ToolEngineEnum.FORM,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.DISCRETE_RATING_CONFIG,
	workRenderer: ToolRendererEnum.DISCRETE_RATING_WORK,
	resultRenderer: ToolRendererEnum.DISCRETE_RATING_RESULT,
	blankPrintRenderer: ToolRendererEnum.DISCRETE_RATING_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.DISCRETE_RATING_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	searchKeywords: ["Wall Question", "aphasia", "life impact", "quality of life"],
	defaultConfig: createWallQuestionConfig(),
	publicDemoConfig: {demoEnginePayload: createWallQuestionDemoPayload(), priorResults: createWallQuestionDemoPriorResults()},
})

// Deprecated for the current Qwoach Wheel of Life tool.
// Keep this definition only for legacy generic-tool compatibility/reference.
// The public Wheel of Life page uses ToolDemoPageCatalog.demoImplementation and
// WheelOfLifePublicDemoAdapter, not these generic engine/renderer implementations.
const wheelOfLifeDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.WHEEL_OF_LIFE,
	slug: "wheel-of-life",
	title: "Wheel of Life",
	shortDescription: "Rates satisfaction and balance across configurable life areas.",
	displaySummary: "Use a profile wheel to identify stronger and weaker life domains.",
	engine: ToolEngineEnum.ITEM_LIST,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.PROFILE_DIMENSION_CONFIG,
	workRenderer: ToolRendererEnum.WHEEL_PROFILE_WORK,
	resultRenderer: ToolRendererEnum.WHEEL_PROFILE_RESULT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	analyticsKey: "wheel_of_life",
	searchKeywords: ["wheel", "life", "balance", "satisfaction", "domains", "profile"],
	defaultConfig: {
		title: "Wheel of Life",
		instructions: "Rate how satisfied you feel with each area right now, then add any context that would help your practitioner understand the rating.",
		scaleMax: 10,
		items: [
			createProfileArea("health", "Health", "Physical wellbeing, energy, sleep, movement, and care for your body."),
			createProfileArea("relationships", "Relationships", "Connection, support, communication, and emotional closeness with important people."),
			createProfileArea("work", "Work / contribution", "Paid work, study, caregiving, volunteering, or other contribution roles."),
			createProfileArea("growth", "Personal growth", "Learning, self-understanding, confidence, and movement toward personal goals."),
			createProfileArea("fun", "Fun / recreation", "Pleasure, play, hobbies, rest, and activities that restore you."),
			createProfileArea("environment", "Environment", "Home, finances, routines, safety, and the practical conditions around you."),
		],
	},
	publicDemoConfig: {
		title: "Wheel of Life",
		instructions: "Rate each life area and review the pattern across domains.",
	},
})

const happinessScaleDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.HAPPINESS_SCALE,
	slug: "happiness-scale",
	title: "Happiness Scale",
	shortDescription: "Review current happiness across life areas in Community Reinforcement Approach treatment.",
	displaySummary: "Compare reported happiness across life areas and repeat the ratings over time.",
	engine: ToolEngineEnum.ITEM_LIST,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.PROFILE_DIMENSION_CONFIG,
	workRenderer: ToolRendererEnum.WHEEL_PROFILE_WORK,
	resultRenderer: ToolRendererEnum.WHEEL_PROFILE_RESULT,
	blankPrintRenderer: ToolRendererEnum.PROFILE_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.PROFILE_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	searchKeywords: ["Happiness Scale", "Community Reinforcement Approach", "CRA", "satisfaction", "life areas"],
	defaultConfig: createHappinessScaleConfig(),
	publicDemoConfig: {demoEnginePayload: createHappinessScaleDemoPayload(), priorResults: createHappinessScaleDemoPriorResults()},
})

const valuesBullseyeDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.VALUES_BULLSEYE,
	slug: "values-bullseye",
	title: "Values Bullseye",
	shortDescription: "Rates how closely current behaviour aligns with chosen values.",
	displaySummary: "Use a bullseye target to map how close current actions are to important values.",
	engine: ToolEngineEnum.ITEM_LIST,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.PROFILE_DIMENSION_CONFIG,
	workRenderer: ToolRendererEnum.BULLSEYE_PROFILE_WORK,
	resultRenderer: ToolRendererEnum.BULLSEYE_PROFILE_RESULT,
	resultBehavior: ToolResultBehaviorEnum.PROFILE_COMPARISON,
	longitudinalClassId: LongitudinalClassId.MULTI_AREA_PROFILE,
	longitudinalRendererId: LongitudinalRendererId.MULTI_AREA_PROFILE,
	supportedSurfaces: sharedFormSurfaces,
	analyticsKey: "values_bullseye",
	searchKeywords: ["values", "bullseye", "alignment", "act", "goals", "priorities"],
	defaultConfig: {
		title: "Values Bullseye",
		instructions: "Place each value on the bullseye based on how closely your recent actions match that value. Closer to the center means more aligned.",
		scaleMax: 10,
		items: [
			createProfileArea("workEducation", "Work/Education", "Workplace, career, education, skills development, and contribution through structured roles."),
			createProfileArea("leisure", "Leisure", "How the client plays, relaxes, enjoys life, rests, recreates, and expresses creativity."),
			createProfileArea("relationships", "Relationships", "Partner, children, parents, relatives, friends, co-workers, and other social contacts."),
			createProfileArea("growthHealth", "Personal Growth/Health", "Spirituality, creativity, life skills, meditation, nature, exercise, nutrition, and health-related choices."),
		],
	},
	publicDemoConfig: {
		title: "Values Bullseye",
		instructions: "Place values on the bullseye and review where actions feel closer or further from what matters.",
	},
})

const moodDiaryDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.MOOD_DIARY,
	slug: "mood-diary",
	title: "Mood Diary",
	shortDescription: "Tracks mood, context, activities, and notes over time.",
	displaySummary: "Use repeated entries to notice mood patterns and context.",
	engine: ToolEngineEnum.TRACKER,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.TRACKER_ENTRY_CONFIG,
	workRenderer: ToolRendererEnum.TRACKER_ENTRY_WORK,
	resultRenderer: ToolRendererEnum.TRACKER_TREND_RESULT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	analyticsKey: "mood_diary",
	searchKeywords: ["mood", "diary", "tracker", "log", "emotion", "patterns"],
	defaultConfig: {
		title: "Mood Diary",
		instructions: "Record one mood entry. Over time, entries can help identify patterns across context, activity, and mood.",
		entryLabel: "Mood entry",
		primaryMeasureElementId: "moodRating",
		entryForm: {
			title: "Mood entry",
			scaleConfig: {
				max: 10,
			},
			fields: [
				createTitleElement("Mood Diary", "Capture what is happening now or what happened during a recent mood shift."),
				createScaleElement("moodRating", "Mood rating", "Very low", "Very good"),
				createTextElement("mainEmotion", "Main emotion or mood word"),
				createMultilineElement("context", "What was happening around this mood?"),
				createMultilineElement("activity", "What were you doing, or what happened next?"),
			],
		},
	},
	publicDemoConfig: {
		title: "Mood Diary",
		instructions: "Create one mood entry and review the saved entry result.",
	},
})

const selfMonitoringFormDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.SELF_MONITORING_FORM,
	slug: "self-monitoring-form",
	title: "Self-Monitoring Form",
	shortDescription: "Record OCD episodes, triggers, intensity, rituals, and ritual time.",
	displaySummary: "Use an episode log to review ERP and response-prevention targets.",
	interactionModel: "Clients record episodes; practitioners review the chronological log and descriptive totals.",
	engine: ToolEngineEnum.TRACKER,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.TRACKER_EPISODE_CONFIG,
	workRenderer: ToolRendererEnum.TRACKER_EPISODE_WORK,
	resultRenderer: ToolRendererEnum.TRACKER_EPISODE_RESULT,
	blankPrintRenderer: ToolRendererEnum.TRACKER_EPISODE_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.TRACKER_EPISODE_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	searchKeywords: ["OCD", "ERP", "EX/RP", "ritual log", "SUDS", "self-monitoring"],
	defaultConfig: createSelfMonitoringConfig(),
	publicDemoConfig: {config: createSelfMonitoringDemoConfig(), demoEnginePayload: createSelfMonitoringDemoPayload()},
})

const momentaryTimeSamplingDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.MOMENTARY_TIME_SAMPLING,
	slug: "momentary-time-sampling",
	title: "Momentary Time Sampling",
	shortDescription: "Estimate behavior occurrence from scores at predetermined moments.",
	displaySummary: "Set a sampling schedule, record exact-moment scores, and review the sampled occurrence estimate.",
	interactionModel: "A recorder scores whether the target behavior is present at each scheduled moment; the practitioner reviews the estimate and missed prompts.",
	engine: ToolEngineEnum.TRACKER,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.TRACKER_MOMENTARY_CONFIG,
	workRenderer: ToolRendererEnum.TRACKER_MOMENTARY_WORK,
	resultRenderer: ToolRendererEnum.TRACKER_MOMENTARY_RESULT,
	blankPrintRenderer: ToolRendererEnum.TRACKER_MOMENTARY_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.TRACKER_MOMENTARY_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	searchKeywords: ["momentary time sampling", "ABA time sampling", "behavior observation", "sampled occurrence"],
	defaultConfig: createMomentaryTimeSamplingConfig(),
	publicDemoConfig: {config: createMomentaryTimeSamplingDemoConfig(), demoEnginePayload: createMomentaryTimeSamplingDemoPayload()},
})

const subjectiveUnitsOfDistressScaleSudsDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.SUBJECTIVE_UNITS_OF_DISTRESS_SCALE_SUDS,
	slug: "subjective-units-of-distress-scale-suds",
	title: "Subjective Units of Distress Scale (SUDS)",
	shortDescription: "Record momentary distress during exposure practice.",
	displaySummary: "Capture repeated SUDS ratings for an exposure target and review the observed trajectory.",
	interactionModel: "The practitioner configures the scale and moments; the client records ratings during an exposure.",
	engine: ToolEngineEnum.TRACKER,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.TRACKER_SUDS_CONFIG,
	workRenderer: ToolRendererEnum.TRACKER_SUDS_WORK,
	resultRenderer: ToolRendererEnum.TRACKER_SUDS_RESULT,
	blankPrintRenderer: ToolRendererEnum.TRACKER_SUDS_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.TRACKER_SUDS_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	searchKeywords: ["SUDS", "subjective units of distress", "ERP", "exposure therapy"],
	defaultConfig: createSudsConfig(),
	publicDemoConfig: {demoEnginePayload: createSudsDemoPayload()},
})

const dbtSkillsDiaryCardDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.DBT_SKILLS_DIARY_CARD,
	slug: "dbt-skills-diary-card",
	title: "DBT Skills Diary Card",
	shortDescription: "Tracks daily DBT ratings, target behaviors, context, skills, and notes across a week.",
	displaySummary: "Use a weekly diary-card grid to review client-reported DBT monitoring data before or during session.",
	interactionModel: "Client completes daily entries across a monitoring week; practitioner reviews the week grid and adds review notes.",
	engine: ToolEngineEnum.TRACKER,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.TRACKER_WEEKLY_DIARY_CONFIG,
	workRenderer: ToolRendererEnum.TRACKER_WEEKLY_DIARY_WORK,
	resultRenderer: ToolRendererEnum.TRACKER_WEEKLY_DIARY_RESULT,
	blankPrintRenderer: ToolRendererEnum.TRACKER_WEEKLY_DIARY_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.TRACKER_WEEKLY_DIARY_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	analyticsKey: "dbt_skills_diary_card",
	searchKeywords: ["dbt", "diary card", "skills", "target behavior", "urges", "emotions", "weekly tracker"],
	defaultConfig: {
		title: "DBT Skills Diary Card",
		instructions: "Record the assigned daily ratings, target behaviors, context, and skills practice for this monitoring week.",
		entryLabel: "Diary day",
		trackerKind: "weeklyDiary",
		weekLength: 7,
		ratingScaleMax: 5,
		days: [
			{id: "day1", label: "Mon"},
			{id: "day2", label: "Tue"},
			{id: "day3", label: "Wed"},
			{id: "day4", label: "Thu"},
			{id: "day5", label: "Fri"},
			{id: "day6", label: "Sat"},
			{id: "day7", label: "Sun"},
		],
		ratingSections: [
			createDiaryRatingSection("urges", "Urges", [
				{id: "urgeSelfHarm", title: "Urge to self-harm", description: "Rate the strength of the urge for this day."},
				{id: "urgeSubstanceUse", title: "Urge to use substances", description: "Rate the strength of the urge for this day."},
				{id: "urgeAvoidance", title: "Urge to avoid", description: "Rate the strength of the urge for this day."},
			]),
			createDiaryRatingSection("emotions", "Emotions", [
				{id: "emotionSadness", title: "Sadness", description: "Rate the intensity for this day."},
				{id: "emotionAnger", title: "Anger", description: "Rate the intensity for this day."},
				{id: "emotionAnxiety", title: "Anxiety", description: "Rate the intensity for this day."},
				{id: "emotionShame", title: "Shame", description: "Rate the intensity for this day."},
			]),
		],
		targetBehaviors: [
			createDiaryConfigItem("targetSelfHarm", "Self-harm behavior", "count", "Enter the number of occurrences for the day."),
			createDiaryConfigItem("targetSubstanceUse", "Substance use behavior", "count", "Enter the number of occurrences for the day."),
			createDiaryConfigItem("targetAvoidance", "Avoidance behavior", "count", "Enter the number of occurrences for the day."),
		],
		contextFields: [
			createDiaryConfigItem("medicationTaken", "Medication taken as planned", "yesNo", "Record whether planned medication was taken for this day."),
			createDiaryConfigItem("substanceContext", "Substance use context", "text", "Optional short note about relevant substance-use context."),
		],
		skills: [
			{id: "mindfulness", title: "Mindfulness", description: "Check if practiced today."},
			{id: "distressTolerance", title: "Distress tolerance", description: "Check if practiced today."},
			{id: "emotionRegulation", title: "Emotion regulation", description: "Check if practiced today."},
			{id: "interpersonalEffectiveness", title: "Interpersonal effectiveness", description: "Check if practiced today."},
			{id: "middlePath", title: "Middle path", description: "Check if practiced today."},
		],
		includeNotes: true,
		includeSessionEntryRatings: true,
		sessionEntryRatings: [
			{id: "sessionPriority", title: "Priority to discuss in session", description: "Rate how important this day feels for session review."},
			{id: "confidenceUsingSkills", title: "Confidence using skills", description: "Rate confidence using skills today."},
		],
		reviewNotesLabel: "Private practitioner notes",
	},
	publicDemoConfig: {
		title: "DBT Skills Diary Card",
		instructions: "Explore a weekly diary card. Entries are not saved as client data.",
		demoEnginePayload: {
			entries: [
				createDiaryEntry("day1", "Mon", {
					ratings: {urgeSelfHarm: 2, urgeSubstanceUse: 1, urgeAvoidance: 3, emotionSadness: 3, emotionAnger: 1, emotionAnxiety: 4, emotionShame: 2},
					targetBehaviors: {targetSelfHarm: 0, targetSubstanceUse: 0, targetAvoidance: 1},
					contextFields: {medicationTaken: true, substanceContext: "None reported"},
					skills: {mindfulness: true, distressTolerance: true},
					notes: "filled in after work.",
					sessionEntryRatings: {sessionPriority: 3, confidenceUsingSkills: 2},
				}),
				createDiaryEntry("day2", "Tue", {
					ratings: {urgeSelfHarm: 1, urgeAvoidance: 2, emotionSadness: 2, emotionAnger: 2, emotionAnxiety: 3, emotionShame: 1},
					targetBehaviors: {targetSelfHarm: 0, targetSubstanceUse: 0, targetAvoidance: 0},
					contextFields: {medicationTaken: true, substanceContext: "None reported"},
					skills: {mindfulness: true, emotionRegulation: true},
					sessionEntryRatings: {sessionPriority: 2, confidenceUsingSkills: 3},
				}),
				createDiaryEntry("day3", "Wed", {
					ratings: {urgeSelfHarm: 3, urgeSubstanceUse: 2, emotionAnxiety: 5},
					targetBehaviors: {targetAvoidance: 2},
					skills: {distressTolerance: true},
				}),
				createDiaryEntry("day4", "Thu"),
				createDiaryEntry("day5", "Fri", {
					ratings: {urgeSelfHarm: 1, urgeSubstanceUse: 1, urgeAvoidance: 1, emotionSadness: 2, emotionAnger: 1, emotionAnxiety: 2, emotionShame: 1},
					targetBehaviors: {targetSelfHarm: 0, targetSubstanceUse: 0, targetAvoidance: 0},
					contextFields: {medicationTaken: true, substanceContext: "None reported"},
					skills: {mindfulness: true, interpersonalEffectiveness: true},
					sessionEntryRatings: {sessionPriority: 1, confidenceUsingSkills: 4},
				}),
			],
		},
	},
})

const nidaDiaryCardDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.NIDA_DIARY_CARD,
	slug: "dbt-substance-use-diary-card",
	title: "DBT Substance Use Diary Card",
	shortDescription: "Tracks daily DBT diary-card data for substance-use, medication/pharmacotherapy, risk behavior, urges, emotions, reinforcement, and skills review.",
	displaySummary: "Use a weekly DBT diary-card preset for substance-use and pharmacotherapy-informed monitoring.",
	interactionModel: "Practitioner confirms applicable preset rows; client completes daily entries across a monitoring week; practitioner reviews the weekly grid and private review notes.",
	engine: ToolEngineEnum.TRACKER,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.TRACKER_WEEKLY_DIARY_CONFIG,
	workRenderer: ToolRendererEnum.TRACKER_WEEKLY_DIARY_WORK,
	resultRenderer: ToolRendererEnum.TRACKER_WEEKLY_DIARY_RESULT,
	blankPrintRenderer: ToolRendererEnum.TRACKER_WEEKLY_DIARY_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.TRACKER_WEEKLY_DIARY_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	analyticsKey: "dbt_substance_use_diary_card",
	searchKeywords: ["nida", "nida diary card", "nida dbt diary card", "dbt substance use diary card", "dbt diary card", "substance use", "pharmacotherapy", "suboxone", "medication adherence", "hiv risk", "skills"],
	warningContent: "This tool records descriptive self-report information only and does not provide medication guidance, risk disposition, diagnosis, or treatment recommendations.",
	defaultConfig: {
		title: "DBT Substance Use Diary Card",
		instructions: "Record the assigned daily ratings, substance-use and medication/pharmacotherapy context, risk behaviors, reinforcement, and skills practice for this monitoring week.",
		entryLabel: "Diary day",
		trackerKind: "weeklyDiary",
		weekLength: 7,
		ratingScaleMax: 5,
		sectionLabels: {
			weeklyFields: "Card details",
			targetBehaviors: "Actions and risk behaviors",
			contextFields: "Substance use and medication context",
			skills: "Skills practiced",
		},
		days: [
			{id: "day1", label: "Mon"},
			{id: "day2", label: "Tue"},
			{id: "day3", label: "Wed"},
			{id: "day4", label: "Thu"},
			{id: "day5", label: "Fri"},
			{id: "day6", label: "Sat"},
			{id: "day7", label: "Sun"},
		],
		weeklyFields: [
			createDiaryConfigItem("filledOutInSession", "Filled out in session", "yesNo", "Record whether this card was completed during session."),
			{
				...createDiaryConfigItem("fillFrequency", "How often was the card filled out?", "select", "Records whether entries were completed daily, a few times during the week, once, or in session."),
				options: [
					{label: "Daily", value: "daily"},
					{label: "2–3 times", value: "2-3"},
					{label: "4–6 times", value: "4-6"},
					{label: "Once", value: "once"},
					{label: "In session", value: "inSession"},
				],
			},
		],
		ratingSections: [
			createDiaryRatingSection("urges", "Highest urges", [
				{id: "urgeCommitSuicide", title: "Urge to end life", description: "Rate the highest urge for this day on the card scale."},
				{id: "urgeSelfHarm", title: "Urge to self-harm", description: "Rate the highest urge for this day on the card scale."},
				{id: "urgeUseDrugs", title: "Urge to use drugs", description: "Rate the highest urge for this day on the card scale."},
				{id: "urgeQuitTherapy", title: "Urge to quit therapy", description: "Rate the highest urge for this day on the card scale."},
			]),
			createDiaryRatingSection("dailyRatings", "Highest daily ratings", [
				{id: "emotionalMisery", title: "Emotional misery", description: "Rate the highest emotional distress for this day."},
				{id: "physicalMisery", title: "Physical misery", description: "Rate the highest physical discomfort for this day."},
				{id: "joy", title: "Joy", description: "Rate the highest joy or positive emotion for this day."},
			]),
		],
		targetBehaviors: [
			createDiaryConfigItem("alcoholUseCount", "Alcohol use episodes", "count", "Enter zero if none occurred; leave blank if not answered."),
			createDiaryConfigItem("illicitDrugUseCount", "Non-prescribed drug use episodes", "count", "Enter zero if none occurred; leave blank if not answered."),
			createDiaryConfigItem("selfHarmCount", "Self-harm behavior", "count", "Enter zero if none occurred; leave blank if not answered."),
			createDiaryConfigItem("lyingCount", "Lying or withholding behavior", "count", "Enter zero if none occurred; leave blank if not answered."),
			createDiaryConfigItem("hivRiskBehavior", "HIV-risk behavior occurred", "yesNo", "Record whether this occurred for the day."),
			createDiaryConfigItem("highRiskBehaviorCount", "High-risk behavior episodes", "count", "Enter zero if none occurred; leave blank if not answered."),
		],
		contextFields: [
			createDiaryConfigItem("alcoholDetails", "Alcohol details", "text", "Record type, amount, or unknown amount details without adding severity labels."),
			createDiaryConfigItem("illicitDrugDetails", "Non-prescribed drug details", "text", "Record type, amount, or unknown amount details without adding severity labels."),
			createDiaryConfigItem("medicationTaken", "Medication taken as planned", "yesNo", "Record whether planned medication or pharmacotherapy was taken for this day."),
			createDiaryConfigItem("suboxoneDoseMg", "Suboxone dose recorded in mg", "count", "Record the reported dose when this field applies."),
			createDiaryConfigItem("prnOrOtcUse", "PRN or OTC medication use", "text", "Record reported as-needed or over-the-counter medication context."),
			{...createDiaryConfigItem("weeksSincePharmacotherapySession", "Weeks since last pharmacotherapy session", "count", "Record the number of weeks if this applies."), range: [0, 52]},
			createDiaryConfigItem("doseChange", "Dose change reported", "yesNo", "Record whether a dose change was reported for this day."),
			createDiaryConfigItem("medicationChangeDetails", "Medication change details", "text", "Record descriptive medication-change context without providing guidance."),
			createDiaryConfigItem("reinforcement", "Reinforcement or consequence", "text", "Record reinforcement, consequence, or context relevant to review."),
		],
		skills: [
			{id: "wiseMind", title: "Wise mind", description: "Check if practiced today."},
			{id: "observe", title: "Observe", description: "Check if practiced today."},
			{id: "describe", title: "Describe", description: "Check if practiced today."},
			{id: "participate", title: "Participate", description: "Check if practiced today."},
			{id: "nonJudgmentalStance", title: "Nonjudgmental stance", description: "Check if practiced today."},
			{id: "oneThingAtATime", title: "One thing at a time", description: "Check if practiced today."},
			{id: "effectiveAction", title: "Effective action", description: "Check if practiced today."},
			{id: "clarifyInterpersonalGoal", title: "Clarify interpersonal goal", description: "Check if practiced today."},
			{id: "askClearly", title: "Ask clearly", description: "Check if practiced today."},
			{id: "relationshipEffectiveness", title: "Relationship effectiveness", description: "Check if practiced today."},
			{id: "selfRespectEffectiveness", title: "Self-respect effectiveness", description: "Check if practiced today."},
			{id: "oppositeAction", title: "Opposite action", description: "Check if practiced today."},
			{id: "problemSolving", title: "Problem solving", description: "Check if practiced today."},
			{id: "positiveActivities", title: "Positive activities", description: "Check if practiced today."},
			{id: "buildMastery", title: "Build mastery", description: "Check if practiced today."},
			{id: "copeAhead", title: "Cope ahead", description: "Check if practiced today."},
			{id: "reduceVulnerability", title: "Reduce vulnerability", description: "Check if practiced today."},
			{id: "urgeSurfing", title: "Urge surfing", description: "Check if practiced today."},
			{id: "radicalAcceptance", title: "Radical acceptance", description: "Check if practiced today."},
			{id: "willingness", title: "Willingness", description: "Check if practiced today."},
			{id: "currentThoughts", title: "Mindfulness of current thoughts", description: "Check if practiced today."},
		],
		includeNotes: true,
		includeSessionEntryRatings: true,
		sessionEntryRatings: [
			{id: "skillsUseScore", title: "Skills-use score", description: "Rate how much skills were used or helped today."},
			{id: "sessionUrgeQuitTherapy", title: "Coming into session: urge to quit therapy", description: "Rate the urge coming into session."},
			{id: "sessionUrgeUseDrugs", title: "Coming into session: urge to use drugs", description: "Rate the urge coming into session."},
			{id: "sessionUrgeEndLife", title: "Coming into session: urge to end life", description: "Rate the urge coming into session."},
			{id: "sessionEmotionControl", title: "Coming into session: emotion self-control", description: "Rate perceived ability to self-regulate emotions."},
			{id: "sessionActionControl", title: "Coming into session: action self-control", description: "Rate perceived ability to self-regulate actions."},
			{id: "sessionThoughtControl", title: "Coming into session: thought self-control", description: "Rate perceived ability to self-regulate thoughts."},
		],
		reviewNotesLabel: "Private practitioner chain-analysis and review notes",
	},
	publicDemoConfig: {
		title: "DBT Substance Use Diary Card",
		instructions: "Explore a weekly DBT substance-use diary card. Entries are not saved as client data.",
		demoEnginePayload: {
			weeklyAnswers: {
				filledOutInSession: false,
				fillFrequency: "daily",
			},
			entries: [
				createDiaryEntry("day1", "Mon", {
					ratings: {urgeCommitSuicide: 1, urgeSelfHarm: 1, urgeUseDrugs: 2, urgeQuitTherapy: 1, emotionalMisery: 2, physicalMisery: 1, joy: 2},
					targetBehaviors: {alcoholUseCount: 0, illicitDrugUseCount: 0, selfHarmCount: 0, lyingCount: 0, hivRiskBehavior: false, highRiskBehaviorCount: 0},
					contextFields: {alcoholDetails: "None reported", illicitDrugDetails: "None reported", medicationTaken: true, suboxoneDoseMg: 8, prnOrOtcUse: "", weeksSincePharmacotherapySession: 1, doseChange: false, medicationChangeDetails: "", reinforcement: "called support person after urge."},
					skills: {wiseMind: true, observe: true, urgeSurfing: true, radicalAcceptance: true},
					notes: "No substance use recorded today.",
					sessionEntryRatings: {skillsUseScore: 5, sessionUrgeQuitTherapy: 1, sessionUrgeUseDrugs: 2, sessionUrgeEndLife: 1, sessionEmotionControl: 3, sessionActionControl: 4, sessionThoughtControl: 3},
				}),
				createDiaryEntry("day2", "Tue", {
					ratings: {urgeCommitSuicide: 1, urgeSelfHarm: 1, urgeUseDrugs: 3, urgeQuitTherapy: 2, emotionalMisery: 3, physicalMisery: 2, joy: 1},
					targetBehaviors: {alcoholUseCount: 1, illicitDrugUseCount: 0, selfHarmCount: 0, lyingCount: 1, hivRiskBehavior: false, highRiskBehaviorCount: 0},
					contextFields: {alcoholDetails: "one drink", illicitDrugDetails: "None reported", medicationTaken: true, suboxoneDoseMg: 8, doseChange: false, medicationChangeDetails: "", reinforcement: "avoided high-risk setting after texting friend."},
					skills: {wiseMind: true, oppositeAction: true, problemSolving: true},
					sessionEntryRatings: {skillsUseScore: 4, sessionUrgeQuitTherapy: 2, sessionUrgeUseDrugs: 3, sessionUrgeEndLife: 1, sessionEmotionControl: 2, sessionActionControl: 3, sessionThoughtControl: 2},
				}),
				createDiaryEntry("day3", "Wed", {
					ratings: {urgeUseDrugs: 2, emotionalMisery: 4, physicalMisery: 1},
					targetBehaviors: {alcoholUseCount: 0, illicitDrugUseCount: 0},
					contextFields: {medicationTaken: null, suboxoneDoseMg: 8, alcoholDetails: "", illicitDrugDetails: ""},
					skills: {willingness: true},
				}),
				createDiaryEntry("day4", "Thu"),
				createDiaryEntry("day5", "Fri", {
					ratings: {urgeCommitSuicide: 1, urgeSelfHarm: 1, urgeUseDrugs: 1, urgeQuitTherapy: 1, emotionalMisery: 2, physicalMisery: 1, joy: 3},
					targetBehaviors: {alcoholUseCount: 0, illicitDrugUseCount: 0, selfHarmCount: 0, lyingCount: 0, hivRiskBehavior: false, highRiskBehaviorCount: 0},
					contextFields: {alcoholDetails: "None reported", illicitDrugDetails: "None reported", medicationTaken: true, suboxoneDoseMg: 8, prnOrOtcUse: "OTC pain relief", doseChange: false, medicationChangeDetails: "", reinforcement: "practiced urge-surfing."},
					skills: {wiseMind: true, relationshipEffectiveness: true, urgeSurfing: true},
					sessionEntryRatings: {skillsUseScore: 5, sessionUrgeQuitTherapy: 1, sessionUrgeUseDrugs: 1, sessionUrgeEndLife: 1, sessionEmotionControl: 4, sessionActionControl: 4, sessionThoughtControl: 4},
				}),
			],
		},
	},
})

const valuesCardSortDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.VALUES_CARD_SORT,
	slug: "values-card-sort",
	title: "Values Exercise",
	shortDescription: "Progressively narrows values cards into a short list of current priorities.",
	displaySummary: "Use progressive narrowing to help clients identify the values that matter most right now.",
	engine: ToolEngineEnum.ITEM_LIST,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.CARD_PROGRESSIVE_NARROWING_CONFIG,
	workRenderer: ToolRendererEnum.CARD_PROGRESSIVE_NARROWING_WORK,
	resultRenderer: ToolRendererEnum.CARD_SELECTED_RESULT,
	resultBehavior: ToolResultBehaviorEnum.STATE_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	analyticsKey: "values_card_sort",
	searchKeywords: ["values", "card", "sort", "priorities", "clarification"],
	defaultConfig: {
		title: "Values Exercise",
		instructions: "A fulfilling life is easier to build when daily choices line up with personal values. Values are guiding principles, qualities, and beliefs that give life meaning. Use this exercise to identify the values that matter most right now.",
		progressiveNarrowingConfig: {
			steps: [
				{targetCount: 20},
				{targetCount: 10},
				{targetCount: 5},
			],
		},
		groups: [],
		items: defaultValuesCardSortItems,
	},
	publicDemoConfig: {
		title: "Values Exercise",
		instructions: "Narrow values cards into a short list of current priorities.",
	},
})

const dbtChainAnalysisDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.DBT_CHAIN_ANALYSIS,
	slug: "dbt-chain-analysis-worksheet",
	title: "DBT Chain Analysis Worksheet",
	shortDescription: "Reconstructs one concrete problem behavior through context, prompting event, step-by-step links, consequences, and alternatives.",
	displaySummary: "Use a DBT-informed staged worksheet to reconstruct a behavior chain and add possible alternatives to selected moments.",
	interactionModel: "Practitioner and client build a staged chain together or asynchronously, then review the same items to add possible alternatives.",
	engine: ToolEngineEnum.STAGED_WORKSHEET,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.STAGED_WORKSHEET_CONFIG,
	workRenderer: ToolRendererEnum.STAGED_WORKSHEET_WORK,
	resultRenderer: ToolRendererEnum.STAGED_WORKSHEET_RESULT,
	blankPrintRenderer: ToolRendererEnum.STAGED_WORKSHEET_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.STAGED_WORKSHEET_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	analyticsKey: "dbt_chain_analysis",
	searchKeywords: [
		"dbt",
		"chain analysis",
		"problem behavior",
		"functional analysis",
		"prompting event",
		"vulnerability factors",
		"intervention points",
	],
	defaultConfig: {
		title: "DBT Chain Analysis Worksheet",
		instructions: "Reconstruct one concrete problem behavior. Add what happened before it, what happened step by step, what followed, and possible places to respond differently next time.",
		alternativePrompt: "What could be different next time?",
		stages: [
			{
				id: "stage_001",
				title: "Behavior",
				description: "Start with one concrete behavior you want to understand.",
				itemLabel: "Behavior",
				itemPlaceholder: "What happened? Keep it concrete and observable.",
			},
			{
				id: "stage_002",
				title: "What made things harder beforehand",
				description: "Add conditions or context that may have made it harder to respond well.",
				itemLabel: "Factor",
				itemPlaceholder: "Example: poor sleep, hunger, conflict, stress, pain, isolation.",
			},
			{
				id: "stage_003",
				title: "What happened right before",
				description: "Add the event, interaction, thought, or circumstance that seemed to start what happened.",
				itemLabel: "Prompting event",
				itemPlaceholder: "What happened right before things started to unfold?",
			},
			{
				id: "stage_004",
				title: "What happened step by step",
				description: "Add each step in plain language.",
				itemLabel: "Step",
				itemPlaceholder: "What happened next?",
			},
			{
				id: "stage_005",
				title: "What happened afterward",
				description: "Add short-term and longer-term consequences or outcomes.",
				itemLabel: "Consequence",
				itemPlaceholder: "What followed the behavior?",
			},
		],
	},
	publicDemoConfig: {
		title: "DBT Chain Analysis Worksheet",
		instructions: "Explore a chain analysis. Entries are not saved as client data.",
		demoEnginePayload: {
			stagedWorksheet: {
				stages: [
					{id: "stage_001", title: "Behavior", alternatives: [], items: [{id: "demo_item_001", text: "Sent several angry replies"}]},
					{id: "stage_002", title: "What made things harder beforehand", alternatives: [], items: [{id: "demo_item_002", text: "Slept poorly the night before"}, {id: "demo_item_003", text: "Had not eaten since morning"}]},
					{id: "stage_003", title: "What happened right before", alternatives: [], items: [{id: "demo_item_004", text: "received a critical text after work"}]},
					{id: "stage_004", title: "What happened step by step", items: [
						{id: "demo_item_005", text: "Read the message several times"},
						{id: "demo_item_006", text: "Thought, 'I always mess this up'"},
						{id: "demo_item_007", text: "Felt heat in face and tightness in chest"},
					], alternatives: [{id: "demo_alt_001", text: "Pause and name it as a thought before replying."}]},
					{id: "stage_005", title: "What happened afterward", alternatives: [], items: [{id: "demo_item_008", text: "Argument continued for another hour"}]},
				],
			},
		},
	},
})

const dbtSolutionAnalysisDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.DBT_SOLUTION_ANALYSIS,
	slug: "dbt-solution-analysis",
	title: "DBT Solution Analysis",
	shortDescription: "Links chain-analysis points to agreed alternatives, prevention steps, environmental changes, practice commitments, and repair actions.",
	displaySummary: "Use a DBT-informed worksheet after or during chain analysis to connect chain points with concrete next steps.",
	interactionModel: "Practitioner and client enter chain points, then add solutions only where they want to work on doing something differently next time.",
	engine: ToolEngineEnum.STAGED_WORKSHEET,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.STAGED_WORKSHEET_CONFIG,
	workRenderer: ToolRendererEnum.STAGED_WORKSHEET_WORK,
	resultRenderer: ToolRendererEnum.STAGED_WORKSHEET_RESULT,
	blankPrintRenderer: ToolRendererEnum.STAGED_WORKSHEET_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.STAGED_WORKSHEET_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	analyticsKey: "dbt_solution_analysis",
	searchKeywords: [
		"dbt",
		"solution analysis",
		"post-chain planning",
		"chain solution analysis",
		"intervention point mapping",
		"skillful alternatives",
		"repair planning",
	],
	defaultConfig: {
		title: "DBT Solution Analysis",
		instructions: "Start from a completed or emerging chain. Add the chain points in the relevant sections, then add solutions only where you want to work on doing something differently next time.",
		alternativePrompt: "What could be tried differently here?",
		stages: [
			{
				id: "solution_stage_001",
				title: "Vulnerability factors",
				description: "Add factors that made the behavior more likely or harder to manage.",
				itemLabel: "Vulnerability factor",
				itemPlaceholder: "What made things harder beforehand?",
			},
			{
				id: "solution_stage_002",
				title: "Prompting event or setting condition",
				description: "Add what set off the chain or what was happening right before it started.",
				itemLabel: "Prompting event",
				itemPlaceholder: "What happened right before the chain began?",
			},
			{
				id: "solution_stage_003",
				title: "Chain links",
				description: "Add thoughts, feelings, body sensations, urges, actions, or events in the sequence.",
				itemLabel: "Chain link",
				itemPlaceholder: "What happened next?",
			},
			{
				id: "solution_stage_004",
				title: "Consequences, harm, or repair areas",
				description: "Add outcomes or consequences that may need prevention, repair, or practice.",
				itemLabel: "Consequence or repair area",
				itemPlaceholder: "What followed, or what needs repair?",
			},
		],
	},
	publicDemoConfig: {
		title: "DBT Solution Analysis",
		instructions: "Explore post-chain planning. Chain points and entries are not saved as client data.",
		demoEnginePayload: {
			stagedWorksheet: {
				stages: [
					{id: "solution_stage_001", alternatives: [{id: "demo_solution_001", text: "Pack an afternoon snack before leaving work."}], items: [
						{id: "demo_item_001", text: "Slept poorly and skipped lunch"},
					]},
					{id: "solution_stage_002", alternatives: [{id: "demo_solution_002", text: "Wait ten minutes before replying and draft the first response in notes."}], items: [
						{id: "demo_item_002", text: "Critical text arrived after a long shift"},
					]},
					{id: "solution_stage_003", alternatives: [{id: "demo_solution_003", text: "Name the thought as a thought before choosing the next action."}], items: [
						{id: "demo_item_003", text: "Thought, 'I always mess this up'"},
					]},
					{id: "solution_stage_004", alternatives: [], items: [
						{id: "demo_item_004", text: "Argument continued for another hour"},
					]},
				],
			},
		},
	},
})

const systematicEvocativeUnfoldingDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.SYSTEMATIC_EVOCATIVE_UNFOLDING,
	slug: "systematic-evocative-unfolding",
	title: "Systematic Evocative Unfolding",
	shortDescription: "Unfold one emotional reaction episode during an emotion-focused therapy session.",
	displaySummary: "Anchor a particular reaction, record the emerging process in the client's words, and review possible targets separately.",
	interactionModel: "Practitioner and client reconstruct one moment flexibly; the practitioner records observations and targets in a private review.",
	engine: ToolEngineEnum.STAGED_WORKSHEET,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.STAGED_WORKSHEET_CONFIG,
	workRenderer: ToolRendererEnum.EPISODE_SEQUENCE_WORK,
	resultRenderer: ToolRendererEnum.EPISODE_SEQUENCE_RESULT,
	blankPrintRenderer: ToolRendererEnum.EPISODE_SEQUENCE_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.EPISODE_SEQUENCE_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	searchKeywords: ["EFT", "emotion-focused therapy", "problematic reaction", "episode unfolding", "emotional process"],
	defaultConfig: {
		title: "Systematic Evocative Unfolding",
		instructions: "Choose one particular reaction that felt confusing or painful. Revisit the moment together and record only what becomes clear.",
		episodePrompt: "What particular situation brought up the reaction?\nWhere were you, and what had just happened?",
		anchorConfirmationPrompt: "This describes one particular moment.",
		reactionPrompt: "What felt puzzling or painful about your reaction in that moment?",
		sequenceHeading: "What emerged, in sequence",
		unknownLabel: "Unknown",
		observationPrompt: "Practitioner observations",
		targetPrompt: "Possible intervention targets",
		orderedAcrossStages: true,
		stages: [
			{id: "situation", title: "Situation", description: "What was happening in the scene?", itemPlaceholder: "A concrete detail from the moment", allowAlternatives: false, options: []},
			{id: "perception", title: "Perception", description: "What stood out or seemed to be happening?", itemPlaceholder: "What was noticed or understood", allowAlternatives: false, options: []},
			{id: "body", title: "Bodily response", description: "What happened in your body?", itemPlaceholder: "A sensation or physical change", allowAlternatives: false, options: []},
			{id: "emotion", title: "Emotion", description: "What feeling was present?", itemPlaceholder: "A feeling that emerged", allowAlternatives: false, options: []},
			{id: "meaning", title: "Meaning", description: "What did this moment mean to you?", itemPlaceholder: "The meaning in your own words", allowAlternatives: false, options: []},
			{id: "tendency", title: "Action tendency", description: "What did you feel moved to do?", itemPlaceholder: "An urge or movement", allowAlternatives: false, options: []},
			{id: "shift", title: "Shifts", description: "Did anything change as you stayed with the moment?", itemPlaceholder: "A change in feeling or bodily sense", allowAlternatives: false, options: []},
			{id: "discovery", title: "Implicit discoveries", description: "What became clearer that you had not noticed before?", itemPlaceholder: "A newly recognized response or meaning", allowAlternatives: false, options: []},
		],
	},
	publicDemoConfig: {
		demoEnginePayload: {
			stagedWorksheet: {
				episodeAnchor: "During Tuesday's project meeting, my manager interrupted while I was explaining the timeline.",
				episodeIsSpecific: true,
				reaction: "I suddenly went quiet and could not finish my update.",
				sequenceOrder: ["demo_perception", "demo_body"],
				stages: [
					{id: "perception", items: [{id: "demo_perception", text: "Everyone was watching me."}]},
					{id: "body", items: [{id: "demo_body", text: "My chest tightened."}]},
				],
			},
		},
	},
})

const missingLinksAnalysisDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.MISSING_LINKS_ANALYSIS,
	slug: "missing-links-analysis",
	title: "Missing-Links Analysis",
	shortDescription: "Uses a fixed DBT-informed decision chain to identify what blocked an expected effective behavior.",
	displaySummary: "Use a yes/no decision chain to distinguish knowledge, willingness, remembering, prompting, follow-through, or other barriers.",
	interactionModel: "Practitioner and client name one missed effective behavior, follow the decision chain, then record the barrier and future plan.",
	engine: ToolEngineEnum.GRAPH,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.GRAPH_CHAIN_CONFIG,
	workRenderer: ToolRendererEnum.GRAPH_CHAIN_WORK,
	resultRenderer: ToolRendererEnum.GRAPH_CHAIN_RESULT,
	blankPrintRenderer: ToolRendererEnum.GRAPH_CHAIN_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.GRAPH_CHAIN_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	analyticsKey: "missing_links_analysis",
	searchKeywords: [
		"dbt",
		"missing links analysis",
		"missing-links analysis",
		"absent effective behavior",
		"missed behavior analysis",
		"corrective plan",
		"behavior analysis decision tree",
	],
	defaultConfig: {
		title: "Missing-Links Analysis",
		instructions: "Focus on one expected effective behavior that did not happen. Answer each question with yes or no, then record the barrier and future plan.",
		graphTemplate: {
			graphKind: "missingLinks",
			outcomeNodeTitle: "Obstacle",
			responseOptions: [
				{value: "yes", label: "Yes"},
				{value: "no", label: "No"},
			],
			fields: [
				{id: "missingBehavior", title: "Expected effective behavior", responseType: "text", isRequired: true},
				{id: "context", title: "Context", responseType: "text"},
				{id: "reportedObstacle", title: "Obstacle", responseType: "text"},
				{id: "correctiveTarget", title: "Corrective target", responseType: "text"},
				{id: "futurePlan", title: "Future plan", responseType: "text"},
			],
			correctiveTargets: [
				{id: "clarification", title: "Clarification"},
				{id: "willingnessWork", title: "Willingness work"},
				{id: "promptingReminders", title: "Prompting or reminders"},
				{id: "skillsPractice", title: "Skills practice"},
				{id: "reinforcement", title: "Reinforcement"},
				{id: "otherProblemSolving", title: "Other problem-solving"},
			],
			nodes: [
				{
					id: "knowledge",
					type: "question",
					data: {
						title: "Knowledge",
						prompt: "Did you know exactly what effective behavior was expected or needed?",
						notePrompt: "What was clear or unclear?",
						isStart: true,
					},
				},
				{
					id: "willingness",
					type: "question",
					data: {
						title: "Willingness",
						prompt: "Were you willing to do the effective behavior in that situation?",
						notePrompt: "What made willingness easier or harder?",
					},
				},
				{
					id: "remembering",
					type: "question",
					data: {
						title: "Remembering",
						prompt: "Did you think of doing the effective behavior at the time?",
						notePrompt: "What did or did not bring it to mind?",
					},
				},
				{
					id: "prompting",
					type: "question",
					data: {
						title: "Prompting",
						prompt: "Did you have a prompt, reminder, or cue that helped you start?",
						notePrompt: "What prompts were present or missing?",
					},
				},
				{
					id: "followThrough",
					type: "question",
					data: {
						title: "Follow-through",
						prompt: "After you thought of it, did you begin right away?",
						notePrompt: "What happened between thinking of it and acting?",
					},
				},
				{
					id: "otherBarrier",
					type: "question",
					data: {
						title: "Other obstacle",
						prompt: "Did another obstacle get in the way?",
						notePrompt: "Describe the obstacle if one fits.",
					},
				},
			],
			edges: [
				{id: "knowledge-yes", type: "response", sourceId: "knowledge", targetId: "willingness", data: {responseValue: "yes"}},
				{id: "knowledge-no", type: "response", sourceId: "knowledge", targetId: "", data: {responseValue: "no", barrierCategory: "knowledge", barrierLabel: "Knowledge or clarity"}},
				{id: "willingness-yes", type: "response", sourceId: "willingness", targetId: "remembering", data: {responseValue: "yes"}},
				{id: "willingness-no", type: "response", sourceId: "willingness", targetId: "", data: {responseValue: "no", barrierCategory: "willingness", barrierLabel: "Willingness"}},
				{id: "remembering-yes", type: "response", sourceId: "remembering", targetId: "prompting", data: {responseValue: "yes"}},
				{id: "remembering-no", type: "response", sourceId: "remembering", targetId: "", data: {responseValue: "no", barrierCategory: "remembering", barrierLabel: "Remembering"}},
				{id: "prompting-yes", type: "response", sourceId: "prompting", targetId: "followThrough", data: {responseValue: "yes"}},
				{id: "prompting-no", type: "response", sourceId: "prompting", targetId: "", data: {responseValue: "no", barrierCategory: "prompting", barrierLabel: "Prompting or reminders"}},
				{id: "follow-through-yes", type: "response", sourceId: "followThrough", targetId: "otherBarrier", data: {responseValue: "yes"}},
				{id: "follow-through-no", type: "response", sourceId: "followThrough", targetId: "", data: {responseValue: "no", barrierCategory: "followThrough", barrierLabel: "Follow-through"}},
				{id: "other-barrier-yes", type: "response", sourceId: "otherBarrier", targetId: "", data: {responseValue: "yes", barrierCategory: "other", barrierLabel: "Other obstacle"}},
				{id: "other-barrier-no", type: "response", sourceId: "otherBarrier", targetId: "", data: {responseValue: "no", barrierCategory: "otherProblemSolving", barrierLabel: "Other problem-solving"}},
			],
		},
	},
	publicDemoConfig: {
		title: "Missing-Links Analysis",
		instructions: "Explore a missing-behavior branch. Demo answers are not saved as client data.",
		demoEnginePayload: {
			graph: {
				nodes: [
					{id: "graph-chain-field-missingBehavior", type: "field", data: {fieldId: "missingBehavior", value: "send the agreed check-in message before bedtime"}},
					{id: "graph-chain-field-context", type: "field", data: {fieldId: "context", value: "The check-in was expected after a stressful day."}},
					{id: "graph-chain-field-reportedObstacle", type: "field", data: {fieldId: "reportedObstacle", value: "The reminder was too easy to miss."}},
					{id: "graph-chain-field-correctiveTarget", type: "field", data: {fieldId: "correctiveTarget", value: "Prompting or reminders"}},
					{id: "graph-chain-field-futurePlan", type: "field", data: {fieldId: "futurePlan", value: "Put the reminder where it is visible during the evening routine."}},
					{id: "knowledge", type: "question", data: {response: {value: "yes", note: "The expected behavior was clear."}}},
					{id: "willingness", type: "question", data: {response: {value: "yes", note: "The plan still seemed worth doing."}}},
					{id: "remembering", type: "question", data: {response: {value: "no", note: "It did not come to mind until the next morning."}}},
				],
				edges: [],
			},
		},
	},
})

const linehanRiskAssessmentAndManagementProtocolLrampDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.LINEHAN_RISK_ASSESSMENT_AND_MANAGEMENT_PROTOCOL_LRAMP,
	slug: "linehan-risk-assessment-and-management-protocol-lramp",
	title: "Linehan Risk Assessment and Management Protocol (LRAMP)",
	shortDescription: "Documents DBT suicide and self-injury risk formulation, management actions, disposition, and reevaluation timing.",
	displaySummary: "Use a structured practitioner-only protocol to document current suicide or self-injury risk concerns, protective factors, actions, disposition, and follow-up timing.",
	interactionModel: "The practitioner completes a configurable structured protocol record, then reviews and prints the completed documentation.",
	engine: ToolEngineEnum.FORM,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.FORM_STANDARD_CONFIG,
	workRenderer: ToolRendererEnum.FORM_STANDARD_WORK,
	resultRenderer: ToolRendererEnum.FORM_STANDARD_RESULT,
	blankPrintRenderer: ToolRendererEnum.FORM_STANDARD_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.FORM_STANDARD_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
	supportedSurfaces: [
		ToolSurfaceEnum.PUBLIC_DEMO,
		ToolSurfaceEnum.PRACTITIONER_TEMPLATE,
		ToolSurfaceEnum.RESULT_REVIEW,
	],
	analyticsKey: "linehan_risk_assessment_and_management_protocol_lramp",
	searchKeywords: [
		"lramp",
		"linehan",
		"dbt",
		"suicide risk",
		"self-injury risk",
		"risk management",
		"risk disposition",
		"reevaluation timing",
	],
	defaultConfig: {
		title: "Linehan Risk Assessment and Management Protocol (LRAMP)",
		instructions: "Complete this practitioner-only protocol when clinically significant suicide or self-injury risk information needs structured documentation. Record unknown or not assessed information directly rather than inferring it.",
		scaleConfig: {
			max: 5,
		},
		fields: [
			createTitleElement("Record context", "Identify the current record and why the protocol is being completed."),
			createTextElement("clientIdentifier", "Client identifier"),
			createDateElement("assessmentDate", "Assessment date", true),
			createCheckboxesElement("reasonForCompletion", "Reason for completion", [
				"Intake or history review",
				"Increased suicidal ideation",
				"Suicide communication",
				"Self-injury concern",
				"Suicide attempt concern",
				"Other clinically significant risk information",
			], true),
			createMultilineElement("incidentCurrentConcern", "Incident or current concern", true),

			createTitleElement("Risk assessment", "Document reported information, observed indicators, and assessment status."),
			createRadioElement("formalAssessmentStatus", "Structured assessment status", [
				"Completed",
				"Partially completed",
				"Not assessed",
				"Unknown",
			], true),
			createMultilineElement("reportedInformation", "Reported suicide or self-injury information"),
			createCheckboxesElement("acuteRiskIndicators", "Acute risk indicators considered", [
				"Current ideation or urges",
				"Intent, planning, or preparation",
				"Recent self-injury",
				"Recent suicide attempt",
				"Access to means",
				"Substance use, agitation, or impulsivity",
				"Major loss, conflict, or worsening stressor",
				"Unknown or not assessed",
			], true),
			createScaleElement("currentConcernRating", "Current clinical concern rating", "Lower concern", "Higher concern", false, 5),
			createMultilineElement("riskIndicatorsNotes", "Risk indicator notes"),

			createTitleElement("Protective factors", "Document protective factors and limitations without treating them as automatic risk reduction."),
			createCheckboxesElement("protectiveFactors", "Protective factors considered", [
				"Reasons for living",
				"Supportive contacts",
				"Commitment to treatment",
				"Skills or coping strategies available",
				"Means safety steps",
				"Future responsibilities or goals",
				"Unknown or not assessed",
			], true),
			createMultilineElement("protectiveFactorsNotes", "Protective factor notes"),

			createTitleElement("Formulation and management", "Record clinical reasoning and actions chosen by the practitioner."),
			createMultilineElement("clinicalRationale", "Clinical rationale and formulation", true),
			createCheckboxesElement("managementActions", "Risk-management actions", [
				"Reviewed or updated safety plan",
				"Means safety action discussed",
				"Skills coaching or coping plan",
				"Increased session/contact frequency",
				"Consultation sought or planned",
				"Emergency evaluation or intervention considered",
				"Emergency action initiated",
				"Action deferred with rationale documented",
			], true),
			createMultilineElement("managementActionNotes", "Management action notes", true),
			createRadioElement("consultationEmergencyDecision", "Consultation or emergency decision", [
				"Consultation completed",
				"Consultation planned",
				"Emergency steps initiated",
				"Emergency steps not indicated",
				"Decision pending",
			], true),
			createMultilineElement("consultationEmergencyRationale", "Consultation or emergency rationale"),

			createTitleElement("Disposition and reevaluation", "Summarize the practitioner's disposition and follow-up timing."),
			createRadioElement("finalDisposition", "Final disposition", [
				"Continue current outpatient plan",
				"Increase monitoring or contact",
				"Consultation or team review required",
				"Emergency evaluation or higher level of care",
				"Disposition pending",
			], true),
			createMultilineElement("dispositionRationale", "Disposition rationale", true),
			createTextElement("reevaluationTiming", "Planned reevaluation timing", true),
			createMultilineElement("followUpPlan", "Follow-up plan or reevaluation notes"),
		],
	},
	publicDemoConfig: {
		title: "Linehan Risk Assessment and Management Protocol (LRAMP)",
		instructions: "Explore a practitioner-only draft record. Demo risk details are not saved as real client data.",
		demoEnginePayload: {
			answers: {
				clientIdentifier: "Alex Morgan",
				assessmentDate: "2026-09-22",
				reasonForCompletion: "Increased suicidal ideation, Suicide communication",
				incidentCurrentConcern: "client reported increased suicidal thoughts during a difficult week and contacted the clinician for support.",
				formalAssessmentStatus: "Partially completed",
				reportedInformation: "Client described urges and denied current intent during the session.",
				acuteRiskIndicators: "Current ideation or urges, Major loss, conflict, or worsening stressor",
				currentConcernRating: 3,
				riskIndicatorsNotes: "Intensity, access to means, and intent require follow-up documentation.",
				protectiveFactors: "Supportive contacts, Commitment to treatment, Skills or coping strategies available",
				protectiveFactorsNotes: "Client identified one supportive contact and willingness to use a coping plan.",
				clinicalRationale: "Risk formulation remains incomplete until disposition and reevaluation timing are documented.",
				managementActions: "Skills coaching or coping plan, Consultation sought or planned",
				managementActionNotes: "review coping steps and consult with supervisor.",
				consultationEmergencyDecision: "Consultation planned",
				consultationEmergencyRationale: "Emergency steps not yet documented for this record.",
				finalDisposition: "",
				dispositionRationale: "",
				reevaluationTiming: "",
				followUpPlan: "",
			},
		},
	},
})

const lsasiBehaviorTypeOptions = createChoiceOptions([
	{label: "Nonsuicidal self-injury", value: "nssi"},
	{label: "Suicide attempt", value: "suicide_attempt"},
	{label: "Ambivalent attempt", value: "ambivalent_attempt"},
	{label: "Intent unclear or unknown", value: "unknown_intent"},
])
const lsasiIntentOptions = createChoiceOptions([
	{label: "No intent to die", value: "no_intent_to_die"},
	{label: "Intent to die", value: "intent_to_die"},
	{label: "Ambivalent intent", value: "ambivalent_intent"},
	{label: "Unknown or not yet coded", value: "unknown"},
])
const lsasiMethodOptions = createChoiceOptions([
	"Cutting or scratching",
	"Overdose or poisoning",
	"Hanging or strangulation",
	"Jumping",
	"Firearm",
	"Burning",
	"Hitting self or object",
	"Other method",
	"Unknown",
])
const lsasiDateAccuracyOptions = createChoiceOptions([
	"Exact date",
	"Approximate date",
	"Year only",
	"Age or life period only",
	"Unknown",
])
const lsasiTreatmentOptions = createChoiceOptions([
	"No medical care",
	"First aid or informal care",
	"Outpatient or urgent care",
	"Emergency department",
	"Hospital admission",
	"Unknown",
])
const lsasiLethalityOptions = createChoiceOptions([
	"No or minimal physical injury",
	"Mild injury",
	"Moderate injury",
	"Severe injury",
	"Potentially life-threatening",
	"Unknown",
])

const lifetimeSuicideAttemptSelfInjuryCountLSasiDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.LIFETIME_SUICIDE_ATTEMPT_SELF_INJURY_COUNT_L_SASI,
	slug: "lifetime-suicide-attempt-self-injury-count-l-sasi",
	title: "Lifetime - Suicide Attempt Self-Injury Count (L-SASI)",
	shortDescription: "Records practitioner-administered lifetime counts for self-injury, suicide attempts, ambivalent attempts, methods, dates, treatment level, and lethality.",
	displaySummary: "Use a practitioner-led interview record to code lifetime suicide and self-injury history and review provisional scoring counts.",
	interactionModel: "Practitioner records client-reported lifetime episodes, coding decisions, and count fields; the result separates raw history, practitioner coding, and derived counts.",
	engine: ToolEngineEnum.STAGED_WORKSHEET,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.STAGED_WORKSHEET_CONFIG,
	workRenderer: ToolRendererEnum.STAGED_WORKSHEET_WORK,
	resultRenderer: ToolRendererEnum.STAGED_WORKSHEET_RESULT,
	blankPrintRenderer: ToolRendererEnum.STAGED_WORKSHEET_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.STAGED_WORKSHEET_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	analyticsKey: "lifetime_suicide_attempt_self_injury_count_l_sasi",
	searchKeywords: [
		"l-sasi",
		"lifetime suicide attempt self injury count",
		"lifetime self-injury assessment",
		"suicide attempt count interview",
		"nonsuicidal self-injury history",
		"dbt suicide self-injury assessment",
	],
	warningContent: "This tool records descriptive lifetime history and scoring counts only. It does not generate current-risk disposition, diagnosis, or treatment recommendations.",
	defaultConfig: {
		title: "Lifetime - Suicide Attempt Self-Injury Count (L-SASI)",
		instructions: "Practitioner-led lifetime interview record. Add one row for each coded episode or episode group. Use unknown or approximate coding when details are unclear.",
		alternativePrompt: "",
		ratingScaleMax: 5,
		scoring: {
			frequencyFieldId: "frequencyCount",
			requiredFieldIds: ["behaviorType", "intentCategory", "methodCode", "frequencyCount"],
			unknownValues: ["unknown", "unknown_intent"],
			totals: [
				{id: "nssiTotal", title: "Nonsuicidal self-injury count", fieldId: "behaviorType", values: ["nssi"]},
				{id: "suicideAttemptTotal", title: "Suicide attempt count", fieldId: "behaviorType", values: ["suicide_attempt"]},
				{id: "ambivalentAttemptTotal", title: "Ambivalent attempt count", fieldId: "behaviorType", values: ["ambivalent_attempt"]},
				{id: "unknownIntentTotal", title: "Unknown-intent count", fieldId: "behaviorType", values: ["unknown_intent"]},
			],
			breakdowns: [
				{id: "methodCounts", title: "Method-specific counts", fieldId: "methodCode", options: lsasiMethodOptions},
				{id: "medicalTreatmentCounts", title: "Medical-treatment level counts", fieldId: "medicalTreatmentLevel", options: lsasiTreatmentOptions},
				{id: "lethalityCounts", title: "Lethality-code counts", fieldId: "lethalityCode", options: lsasiLethalityOptions},
			],
		},
		stages: [
			{
				id: "lifetime_history_entries",
				title: "Lifetime self-injury and suicidal-behavior history",
				description: "Record client-reported episodes or episode groups, then apply practitioner coding for intent, method, treatment, date accuracy, and lethality.",
				itemLabel: "Episode or episode group",
				itemPlaceholder: "Brief reported history or interview note",
				allowAlternatives: false,
				printBlankRows: 5,
				itemFields: [
					createStagedEntryField("behaviorType", "Behavior category", "select", {options: lsasiBehaviorTypeOptions, isRequired: true}),
					createStagedEntryField("intentCategory", "Intent or ambivalence", "select", {options: lsasiIntentOptions, isRequired: true}),
					createStagedEntryField("methodCode", "Method code", "select", {options: lsasiMethodOptions, isRequired: true}),
					createStagedEntryField("frequencyCount", "Frequency count", "count", {isRequired: true}),
					createStagedEntryField("episodeDate", "Episode date or anchor", "date"),
					createStagedEntryField("dateAccuracy", "Date accuracy", "select", {options: lsasiDateAccuracyOptions}),
					createStagedEntryField("medicalTreatmentLevel", "Medical treatment level", "select", {options: lsasiTreatmentOptions}),
					createStagedEntryField("lethalityCode", "Lethality code", "select", {options: lsasiLethalityOptions}),
					createStagedEntryField("codingNotes", "Practitioner coding notes", "multiline"),
				],
			},
		],
	},
	publicDemoConfig: {
		title: "Lifetime - Suicide Attempt Self-Injury Count (L-SASI)",
		instructions: "Explore a practitioner-led L-SASI record. History and scoring are not saved as client data.",
		demoEnginePayload: {
			stagedWorksheet: {
				stages: [
					{
						id: "lifetime_history_entries",
						alternatives: [],
						items: [
							{
								id: "demo_lsasi_entry_001",
								text: "one reported adolescent self-injury episode group.",
								values: {
									behaviorType: "nssi",
									intentCategory: "no_intent_to_die",
									methodCode: "cutting_or_scratching",
									frequencyCount: 3,
									episodeDate: "2018-06-01",
									dateAccuracy: "year_only",
									medicalTreatmentLevel: "no_medical_care",
									lethalityCode: "mild_injury",
									codingNotes: "Approximate year only; no intent to die reported during this episode.",
								},
							},
							{
								id: "demo_lsasi_entry_002",
								text: "one overdose event requiring emergency evaluation.",
								values: {
									behaviorType: "suicide_attempt",
									intentCategory: "intent_to_die",
									methodCode: "overdose_or_poisoning",
									frequencyCount: 1,
									episodeDate: "2021-02-15",
									dateAccuracy: "approximate_date",
									medicalTreatmentLevel: "emergency_department",
									lethalityCode: "moderate_injury",
									codingNotes: "Treatment level and lethality were recorded during clinician review.",
								},
							},
							{
								id: "demo_lsasi_entry_003",
								text: "intent unclear pending additional interview probing.",
								values: {
									behaviorType: "unknown_intent",
									intentCategory: "unknown",
									methodCode: "unknown",
									frequencyCount: 1,
									dateAccuracy: "unknown",
									medicalTreatmentLevel: "unknown",
									lethalityCode: "unknown",
									codingNotes: "Provisional scoring remains visible because required coding is unknown.",
								},
							},
						],
					},
				],
			},
		},
	},
})

const imminentRiskAndActionPlanDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.IMMINENT_RISK_AND_ACTION_PLAN,
	slug: "imminent-risk-and-action-plan-brtc-imminent-suicide-risk-and-treatment-actions-note",
	title: "Imminent Risk and Action Plan / BRTC Imminent Suicide Risk and Treatment Actions Note",
	shortDescription: "Documents DBT imminent suicide or self-injury risk concern, treatment actions, emergency decisions, consultation, responsibility, and reevaluation timing.",
	displaySummary: "Use a practitioner-only structured note to connect current imminent-risk concerns with documented actions, consultation, follow-up responsibility, and reevaluation deadline.",
	interactionModel: "The practitioner completes a configurable structured action note, then reviews and prints the completed documentation.",
	engine: ToolEngineEnum.FORM,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.FORM_STANDARD_CONFIG,
	workRenderer: ToolRendererEnum.FORM_STANDARD_WORK,
	resultRenderer: ToolRendererEnum.FORM_STANDARD_RESULT,
	blankPrintRenderer: ToolRendererEnum.FORM_STANDARD_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.FORM_STANDARD_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
	supportedSurfaces: [
		ToolSurfaceEnum.PUBLIC_DEMO,
		ToolSurfaceEnum.PRACTITIONER_TEMPLATE,
		ToolSurfaceEnum.RESULT_REVIEW,
	],
	analyticsKey: "imminent_risk_and_action_plan",
	searchKeywords: [
		"imminent risk",
		"brtc",
		"dbt",
		"suicide risk",
		"self-injury risk",
		"treatment actions",
		"emergency intervention",
		"consultation",
		"reevaluation deadline",
	],
	warningContent: "This tool supports practitioner documentation only. It does not determine suicide risk level, prescribe emergency action, or replace clinical judgement.",
	defaultConfig: {
		title: "Imminent Risk and Action Plan / BRTC Imminent Suicide Risk and Treatment Actions Note",
		instructions: "Complete this practitioner-only note when a current or increased suicide or self-injury risk concern requires immediate documentation and treatment response. Record unknown or pending information directly rather than inferring it.",
		scaleConfig: {
			max: 5,
		},
		fields: [
			createTitleElement("Record context", "Identify the current note, contact, and people responsible for the documentation."),
			createTextElement("clientIdentifier", "Client identifier"),
			createTextElement("practitionerIdentifier", "Practitioner identifier"),
			createDateElement("noteDate", "Note date", true),
			createTextElement("contactContext", "Contact context"),

			createTitleElement("Current imminent-risk concern", "Document why this action note is being completed now."),
			createCheckboxesElement("concernTriggers", "Reason for current concern", [
				"Current suicidal ideation or urges",
				"Suicide threat or communication",
				"Suicide attempt concern",
				"Self-injury or medically serious self-injury concern",
				"Increased risk information",
				"Other immediate clinical concern",
				"Unknown or not fully assessed",
			], true),
			createMultilineElement("currentConcernSummary", "Current concern summary", true),
			createRadioElement("assessmentStatus", "Risk assessment status", [
				"Completed for this contact",
				"Partially completed",
				"Emergency action occurred before full documentation",
				"Pending further information",
				"Unknown or not assessed",
			], true),
			createMultilineElement("reportedInformation", "Reported information and practitioner observations"),

			createTitleElement("Risk and protective factors", "Record current factors specific to this presentation."),
			createCheckboxesElement("currentRiskFactors", "Current risk factors", [
				"Ideation, threat, plan, intent, or preparation",
				"Recent self-injury or suicide attempt",
				"Access to lethal means",
				"Substance use, agitation, impulsivity, or dissociation",
				"Major loss, conflict, isolation, or worsening stressor",
				"Treatment-interfering behavior or dropout concern",
				"Unknown or unavailable",
			], true),
			createMultilineElement("riskFactorNotes", "Risk factor notes", true),
			createMultilineElement("practitionerFormulation", "Practitioner risk formulation", true),
			createCheckboxesElement("protectiveFactors", "Protective factors", [
				"Reasons for living",
				"Client commitment to action plan or treatment",
				"Supportive contact available",
				"Skills or coping strategies available",
				"Lethal-means safety step possible or completed",
				"Future responsibility, goal, or value",
				"Unknown or unavailable",
			], true),
			createMultilineElement("protectiveFactorNotes", "Protective factor notes"),

			createTitleElement("Treatment actions", "Document treatment actions considered, taken, pending, or not used with rationale."),
			createCheckboxesElement("treatmentActions", "Treatment actions documented", [
				"Crisis or problem-solving intervention",
				"Skills coaching or coping plan",
				"Commitment to a plan of action",
				"Lethal-means removal or safety step",
				"Social-support contact or support plan",
				"Increased contact, session frequency, or monitoring",
				"Emergency intervention considered",
				"Action not used or deferred with rationale documented",
			], true),
			createMultilineElement("treatmentActionNotes", "Treatment action notes", true),
			createMultilineElement("crisisProblemSolvingActions", "Crisis or problem-solving interventions"),
			createMultilineElement("planOfActionCommitments", "Plan-of-action commitments"),
			createRadioElement("clientCommitmentStatus", "Client commitment status", [
				"Commitment obtained",
				"Partial commitment or ambivalence documented",
				"Declined commitment",
				"Not applicable to this contact",
				"Pending or unknown",
			]),

			createTitleElement("Means safety and social support", "Document concrete safety steps and support involvement where applicable."),
			createRadioElement("lethalMeansStatus", "Lethal-means safety status", [
				"Removal or restriction completed",
				"Removal or restriction planned",
				"Discussed and declined",
				"Not applicable based on current information",
				"Unknown or pending",
			]),
			createMultilineElement("lethalMeansNotes", "Lethal-means safety notes"),
			createCheckboxesElement("socialSupportActions", "Social support actions", [
				"Support person identified",
				"Support person contacted",
				"Support contact planned",
				"Client declined support involvement",
				"No support available",
				"Pending or unknown",
			]),
			createMultilineElement("socialSupportNotes", "Social support notes"),

			createTitleElement("Emergency intervention and consultation", "Document emergency decisions, actions, rationale, and consultation."),
			createRadioElement("emergencyInterventionDecision", "Emergency intervention decision", [
				"Emergency intervention initiated",
				"Hospitalization or emergency evaluation considered",
				"Higher level of care considered or arranged",
				"Emergency intervention not used with rationale documented",
				"Decision pending",
			], true),
			createMultilineElement("emergencyInterventionNotes", "Emergency intervention notes or rationale", true),
			createRadioElement("consultationStatus", "Consultation status", [
				"Consultation completed",
				"Consultation attempted",
				"Consultation planned",
				"Consultation not used with rationale documented",
				"Pending",
			], true),
			createTextElement("consultationSource", "Consultation source or second opinion"),
			createMultilineElement("consultationNotes", "Consultation notes"),

			createTitleElement("Responsibility and reevaluation", "These fields are central to completion and should remain visible until entered."),
			createTextElement("followUpResponsibility", "Follow-up responsibility", true),
			createTextElement("reevaluationDeadline", "Reevaluation time or deadline", true),
			createMultilineElement("followUpNotes", "Follow-up or reevaluation notes"),
			createMultilineElement("unresolvedCriticalSections", "Unresolved critical sections or pending information"),
		],
	},
	publicDemoConfig: {
		title: "Imminent Risk and Action Plan / BRTC Imminent Suicide Risk and Treatment Actions Note",
		instructions: "Explore a practitioner-only action note. Demo risk details are not saved as real client data.",
		demoEnginePayload: {
			answers: {
				clientIdentifier: "Alex Morgan",
				practitionerIdentifier: "Jordan Lee",
				noteDate: "2026-09-22",
				contactContext: "urgent between-session phone contact",
				concernTriggers: "Current suicidal ideation or urges, Increased risk information",
				currentConcernSummary: "client reported increased suicidal thoughts during a difficult week and requested support before the next session.",
				assessmentStatus: "Partially completed",
				reportedInformation: "Client described urges and named one support person. Intent, access to means, and action follow-through require additional documentation.",
				currentRiskFactors: "Ideation, threat, plan, intent, or preparation, Major loss, conflict, isolation, or worsening stressor",
				riskFactorNotes: "increased intensity reported after conflict; means-safety details are pending.",
				practitionerFormulation: "imminent-risk formulation remains incomplete until consultation, means-safety information, and reevaluation responsibility are documented.",
				protectiveFactors: "Client commitment to action plan or treatment, Supportive contact available, Skills or coping strategies available",
				protectiveFactorNotes: "Client identified one support person and willingness to use a coping plan.",
				treatmentActions: "Crisis or problem-solving intervention, Skills coaching or coping plan, Consultation planned",
				treatmentActionNotes: "review coping steps, identify next contact, and consult with supervisor.",
				crisisProblemSolvingActions: "Practiced paced breathing and identified the next concrete support step.",
				planOfActionCommitments: "Client agreed to remain in contact and use the documented support step during the session.",
				clientCommitmentStatus: "Partial commitment or ambivalence documented",
				lethalMeansStatus: "Unknown or pending",
				lethalMeansNotes: "Needs follow-up before the note is complete.",
				socialSupportActions: "Support person identified, Support contact planned",
				socialSupportNotes: "Support contact is pending.",
				emergencyInterventionDecision: "Hospitalization or emergency evaluation considered",
				emergencyInterventionNotes: "Emergency intervention decision requires updated information and consultation.",
				consultationStatus: "Consultation planned",
				consultationSource: "Supervisor consultation pending",
				consultationNotes: "",
				followUpResponsibility: "",
				reevaluationDeadline: "",
				followUpNotes: "",
				unresolvedCriticalSections: "Follow-up responsibility and reevaluation deadline are intentionally blank in this demo.",
			},
		},
	},
})

const gradedTaskAssignmentDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.GRADED_TASK_ASSIGNMENT,
	slug: "graded-task-assignment",
	title: "Graded Task Assignment",
	shortDescription: "Break an overwhelming non-fear-based goal into achievable steps.",
	displaySummary: "Build an ordered sequence and focus on the next achievable action.",
	interactionModel: "The client names a goal, adds and orders small actions, records progress, and adjusts step size with a practitioner.",
	engine: ToolEngineEnum.STAGED_WORKSHEET,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.GRADED_PLAN_CONFIG,
	workRenderer: ToolRendererEnum.GRADED_PLAN_WORK,
	resultRenderer: ToolRendererEnum.GRADED_PLAN_RESULT,
	blankPrintRenderer: ToolRendererEnum.GRADED_PLAN_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.GRADED_PLAN_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	searchKeywords: ["CBT", "graded task", "behavioral activation", "small steps", "action plan"],
	defaultConfig: {
		gradedPlan: true,
		title: "Graded Task Assignment",
		instructions: "Choose one goal that feels too large right now. Break it into manageable actions, starting with one small enough for your current energy and time. Focus on the next achievable action; later steps can change after you try it.",
		completionMessage: "",
		nextStepLabel: "Next achievable action",
		reviewTitle: "Graded action plan",
		adjustmentPrompt: "What happened when you tried this step?",
		incompleteLabel: "Add a goal and a first action to make this plan ready.",
		noRemainingLabel: "No remaining action.",
		completeLabel: "Done",
		attemptedLabel: "Tried",
		notCompleteLabel: "To do",
		earlierLabel: "Earlier",
		blankStepRows: 5,
		guidance: [],
		stages: [
			{id: "goal", title: "Larger goal", description: "What larger goal are you working toward?", itemPlaceholder: "Name the goal", allowAlternatives: false, minimumItems: 1},
			{id: "steps", title: "Steps toward the goal", description: "What small action can you take first? Add later steps in a gradual order.", itemPlaceholder: "A concrete action", allowAlternatives: false, minimumItems: 1},
		],
	},
	publicDemoConfig: {
		demoEnginePayload: {stagedWorksheet: {stages: [
			{id: "goal", items: [{id: "demo_goal", text: "Find an apartment"}], alternatives: []},
			{id: "steps", items: [
				{id: "demo_step_1", text: "Talk to my neighbor about moving", values: {isComplete: false}},
				{id: "demo_step_2", text: "Figure out how much rent I can afford", values: {isComplete: false}},
				{id: "demo_step_3", text: "Look online for apartments", values: {isComplete: false}},
			], alternatives: []},
		]}},
	},
})

const cciGradedTasksAndExposureDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.CCI_GRADED_TASKS_AND_EXPOSURE,
	slug: "cci-graded-tasks-and-graded-exposure-worksheet",
	title: "Graded Tasks and Graded Exposure Worksheet",
	shortDescription: "Break one difficult goal into manageable, ordered steps.",
	displaySummary: "Plan a graded task or exposure ladder with optional reported distress ratings.",
	interactionModel: "The client defines one goal, orders smaller steps, and records progress for practitioner review.",
	engine: ToolEngineEnum.STAGED_WORKSHEET,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.GRADED_PLAN_CONFIG,
	workRenderer: ToolRendererEnum.GRADED_PLAN_WORK,
	resultRenderer: ToolRendererEnum.GRADED_PLAN_RESULT,
	blankPrintRenderer: ToolRendererEnum.GRADED_PLAN_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.GRADED_PLAN_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	searchKeywords: ["CBT", "graded tasks", "graded exposure", "ladder", "distress"],
	defaultConfig: {
		gradedPlan: true,
		requireReadyOnSubmit: true,
		useType: "gradedTask",
		taskLabel: "Graded task",
		exposureLabel: "Graded exposure",
		ratingsEnabled: true,
		ratingScaleMax: 100,
		ratingLabel: "Distress (if relevant)",
		title: "Graded Tasks and Graded Exposure Worksheet",
		instructions: "Break your goal into more manageable steps to work toward over time.",
		completionMessage: "Work through the steps over time. Adjust the ladder together when a step needs to be smaller or larger.",
		nextStepLabel: "Next step",
		reviewTitle: "Graded ladder",
		adjustmentPrompt: "What happened when you tried this step?",
		incompleteLabel: "Add one goal and at least one step to make this ladder ready.",
		noRemainingLabel: "All steps completed.",
		completeLabel: "Completed",
		attemptedLabel: "Attempted",
		notCompleteLabel: "To do",
		earlierLabel: "Earlier version",
		blankStepRows: 5,
		guidance: [],
		stages: [
			{id: "goal", title: "Goal", description: "What goal are you working toward?", itemPlaceholder: "Enter one goal", allowAlternatives: false, minimumItems: 1},
			{id: "steps", title: "Steps", description: "What smaller steps could take you toward this goal? Put the most manageable step first.", itemPlaceholder: "Enter a manageable step", allowAlternatives: false, minimumItems: 1},
		],
	},
	publicDemoConfig: {demoEnginePayload: {stagedWorksheet: {stages: [
		{id: "goal", items: [{id: "pool_goal", text: "Return to the local swimming pool", values: {rating: 80}}], alternatives: []},
		{id: "steps", items: [
			{id: "pool_step_1", text: "Pack a swim bag", values: {rating: 20}},
			{id: "pool_step_2", text: "Walk to the pool entrance", values: {rating: 45}},
			{id: "pool_step_3", text: "Swim for ten minutes", values: {rating: 70}},
		], alternatives: []},
	]}}},
})

const patientExRpAdherenceScaleDefinition = new ToolDefinition({
	toolType: ToolTypeEnum.PATIENT_EX_RP_ADHERENCE_SCALE_PEAS,
	slug: "patient-ex-rp-adherence-scale-peas",
	title: "Patient EX/RP Adherence Scale (PEAS)",
	shortDescription: "Clinician ratings of exposure quantity, exposure quality, and response prevention between sessions.",
	displaySummary: "Review ERP homework evidence, rate three anchored adherence items, and compare session means over time.",
	interactionModel: "The practitioner reviews homework and enters three clinician ratings with session context and notes.",
	engine: ToolEngineEnum.FORM,
	participationMode: ToolParticipationModeEnum.SINGLE,
	configRenderer: ToolRendererEnum.ANCHORED_MEAN_CONFIG,
	workRenderer: ToolRendererEnum.ANCHORED_MEAN_WORK,
	resultRenderer: ToolRendererEnum.ANCHORED_MEAN_RESULT,
	blankPrintRenderer: ToolRendererEnum.ANCHORED_MEAN_BLANK_PRINT,
	resultPrintRenderer: ToolRendererEnum.ANCHORED_MEAN_RESULT_PRINT,
	resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
	supportedSurfaces: sharedFormSurfaces,
	searchKeywords: ["PEAS", "ERP homework", "exposure response prevention", "adherence"],
	defaultConfig: createPatientExRpAdherenceScaleConfig(),
	publicDemoConfig: {demoEnginePayload: createPatientExRpAdherenceScaleDemoPayload(), priorResults: createPatientExRpAdherenceScaleDemoPriorResults()},
})

const toolDefinitionsArray = [
	new ToolDefinition({
		toolType: ToolTypeEnum.FEATURE_MATCHING_ASSESSMENT,
		slug: "feature-matching-assessment",
		title: "AAC Feature-Matching Assessment",
		shortDescription: "Link documented communication and access needs to AAC system-feature requirements and rationale.",
		displaySummary: "Document user needs, name required AAC features, and review rationale and dimension coverage.",
		interactionModel: "A practitioner records documented needs and contexts, then links system-feature requirements to each need with rationale.",
		engine: ToolEngineEnum.STAGED_WORKSHEET,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.LINKED_REQUIREMENTS_CONFIG,
		workRenderer: ToolRendererEnum.LINKED_REQUIREMENTS_WORK,
		resultRenderer: ToolRendererEnum.LINKED_REQUIREMENTS_RESULT,
		blankPrintRenderer: ToolRendererEnum.LINKED_REQUIREMENTS_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.LINKED_REQUIREMENTS_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["AAC", "feature matching", "system requirements", "communication access"],
		defaultConfig: createFeatureMatchingAssessmentConfig(),
		publicDemoConfig: {demoEnginePayload: createFeatureMatchingAssessmentDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.ARE_QUESTIONNAIRE,
		slug: "a-r-e-questionnaire",
		title: "A.R.E. Questionnaire",
		shortDescription: "Compare how each partner experiences accessibility, responsiveness, and engagement.",
		displaySummary: "Invite each partner to answer privately, then review their reported attachment-bond perceptions together.",
		interactionModel: "Each partner answers the same configurable questionnaire independently; the practitioner reviews both perspectives.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.INDEPENDENT_MULTI,
		participationPolicy: {roles: [
			{id: "partnerA", title: "Partner 1", capabilities: {canWork: true, canViewResult: false}, allowsMultipleAssignments: false},
			{id: "partnerB", title: "Partner 2", capabilities: {canWork: true, canViewResult: false}, allowsMultipleAssignments: false},
		]},
		configRenderer: ToolRendererEnum.PAIRED_QUESTIONNAIRE_CONFIG,
		workRenderer: ToolRendererEnum.PAIRED_QUESTIONNAIRE_WORK,
		resultRenderer: ToolRendererEnum.PAIRED_QUESTIONNAIRE_RESULT,
		blankPrintRenderer: ToolRendererEnum.PAIRED_QUESTIONNAIRE_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.PAIRED_QUESTIONNAIRE_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.SCORED_INTERPRETATION,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["A.R.E.", "EFCT", "emotionally focused couples therapy", "attachment bond", "Accessibility Responsiveness Engagement"],
		defaultConfig: createAREQuestionnaireConfig(),
		publicDemoConfig: {demoEnginePayload: createAREQuestionnaireDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.SCHEMA_THERAPY_CASE_CONCEPTUALIZATION_FORM,
		slug: "schema-therapy-case-conceptualization-form",
		title: "Schema Therapy Case Conceptualization Form",
		shortDescription: "Organize an individual schema therapy case formulation across history, needs, schemas, modes, relationships, and treatment objectives.",
		displaySummary: "Draft and review a practitioner-authored individual case conceptualization.",
		interactionModel: "The practitioner records and revises a structured formulation, then reviews a dated snapshot.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.FORM_STRUCTURED_CONFIG,
		workRenderer: ToolRendererEnum.FORM_STRUCTURED_WORK,
		resultRenderer: ToolRendererEnum.FORM_STRUCTURED_RESULT,
		blankPrintRenderer: ToolRendererEnum.FORM_STRUCTURED_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.FORM_STRUCTURED_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["schema therapy", "case conceptualization", "schema modes", "formulation"],
		defaultConfig: createSchemaTherapyCaseConceptualizationConfig(),
		publicDemoConfig: {demoEnginePayload: createSchemaTherapyCaseConceptualizationDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.EFT_CASE_FORMULATION,
		slug: "eft-case-formulation",
		title: "EFT Case Formulation",
		shortDescription: "Build and revise an Emotion-Focused Therapy treatment map across sessions.",
		displaySummary: "Link presenting difficulties, emotional processes, change targets, and tentative therapeutic tasks.",
		interactionModel: "The practitioner records a provisional formulation, reviews gaps, and revises the treatment direction as new information emerges.",
		engine: ToolEngineEnum.STAGED_WORKSHEET,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.CONTEXT_MAP_CONFIG,
		workRenderer: ToolRendererEnum.CONTEXT_MAP_WORK,
		resultRenderer: ToolRendererEnum.CONTEXT_MAP_RESULT,
		blankPrintRenderer: ToolRendererEnum.CONTEXT_MAP_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.CONTEXT_MAP_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["EFT case formulation", "Emotion-Focused Therapy", "treatment map", "emotion schemes"],
		defaultConfig: createEftCaseFormulationConfig(),
		publicDemoConfig: {demoEnginePayload: createEftCaseFormulationDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.DECONSTRUCTION_QUESTIONS,
		slug: "deconstruction-questions",
		title: "Deconstruction Questions",
		shortDescription: "Explore a fixed problem meaning in relation to its social, relational, cultural, historical, and power contexts.",
		displaySummary: "Build a contextual meaning map with the client's words, uncertain links, and emerging alternatives.",
		interactionModel: "Practitioner and client name a focus meaning, enter contextual links, and discuss possible alternative positions.",
		engine: ToolEngineEnum.STAGED_WORKSHEET,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.CONTEXT_MAP_CONFIG,
		workRenderer: ToolRendererEnum.CONTEXT_MAP_WORK,
		resultRenderer: ToolRendererEnum.CONTEXT_MAP_RESULT,
		blankPrintRenderer: ToolRendererEnum.CONTEXT_MAP_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.CONTEXT_MAP_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["deconstruction questions", "narrative therapy", "contextual meaning map", "problem story"],
		defaultConfig: createDeconstructionQuestionsConfig(),
		publicDemoConfig: {demoEnginePayload: createDeconstructionQuestionsDemoPayload()},
	}),
	patientExRpAdherenceScaleDefinition,
	new ToolDefinition({
		toolType: ToolTypeEnum.OCAIRS,
		slug: "ocairs",
		title: "OCAIRS occupational adaptation profile",
		shortDescription: "Practitioner-led occupational participation interview and profile.",
		displaySummary: "Record reported information, observations and interpretation across twelve assessment areas, then review a dated profile.",
		interactionModel: "A practitioner records interview information and rates each area when enough information is available.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.ORDINAL_PROFILE_CONFIG,
		workRenderer: ToolRendererEnum.ORDINAL_PROFILE_WORK,
		resultRenderer: ToolRendererEnum.ORDINAL_PROFILE_RESULT,
		blankPrintRenderer: ToolRendererEnum.ORDINAL_PROFILE_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.ORDINAL_PROFILE_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["OCAIRS", "MOHO", "occupational participation", "occupational adaptation"],
		defaultConfig: createOcairsConfig(),
		publicDemoConfig: {demoEnginePayload: createOcairsDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.MOHOST,
		slug: "mohost",
		title: "MOHOST Occupational Participation Screening",
		shortDescription: "Structured occupational participation screening profile for occupational therapy practitioners.",
		displaySummary: "Record an occupational participation profile, review item ratings and comments, and compare repeated assessments.",
		interactionModel: "The practitioner records ratings, context, comments and a strengths and limitations analysis.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.ORDINAL_PROFILE_CONFIG,
		workRenderer: ToolRendererEnum.ORDINAL_PROFILE_WORK,
		resultRenderer: ToolRendererEnum.ORDINAL_PROFILE_RESULT,
		blankPrintRenderer: ToolRendererEnum.ORDINAL_PROFILE_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.ORDINAL_PROFILE_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["MOHOST", "MOHO", "occupational participation", "occupational therapy screening"],
		defaultConfig: createMohostConfig(),
		publicDemoConfig: {demoEnginePayload: createMohostDemoPayload(), priorResults: createMohostPriorResults()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.SCHEMA_MODE_INVENTORY,
		slug: "schema-mode-inventory-smi-1-1-short-smi",
		title: "Schema Mode Inventory — adult short SMI",
		shortDescription: "Adult schema mode frequency ratings and a 14-mode profile.",
		displaySummary: "Collect 118 frequency ratings and review relative schema mode means.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.SUBSCALE_PROFILE_CONFIG,
		workRenderer: ToolRendererEnum.SUBSCALE_PROFILE_WORK,
		resultRenderer: ToolRendererEnum.SUBSCALE_PROFILE_RESULT,
		blankPrintRenderer: ToolRendererEnum.SUBSCALE_PROFILE_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.SUBSCALE_PROFILE_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.SCORED_INTERPRETATION,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["Schema Mode Inventory", "SMI", "adult schema modes", "short SMI"],
		defaultConfig: createSchemaModeInventoryConfig(),
		publicDemoConfig: {instructions: "Rate the statements and review the mode profile.", config: createSchemaModeInventoryDemoConfig(), demoEnginePayload: createSchemaModeInventoryDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.YOUNG_SCHEMA_QUESTIONNAIRE_L3,
		slug: "young-schema-questionnaire-long-form-3rd-edition-ysq-l3",
		title: "Young Schema Questionnaire — Long Form, 3rd Edition (YSQ-L3)",
		shortDescription: "Rate 232 statements and review an 18-schema profile.",
		displaySummary: "Collect long-form ratings and review schema scores with descriptive interpretation bands.",
		interactionModel: "The client rates each statement; the practitioner reviews scored schema categories and adds their own interpretation.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.SUBSCALE_PROFILE_CONFIG,
		workRenderer: ToolRendererEnum.SUBSCALE_PROFILE_WORK,
		resultRenderer: ToolRendererEnum.SUBSCALE_PROFILE_RESULT,
		blankPrintRenderer: ToolRendererEnum.SUBSCALE_PROFILE_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.SUBSCALE_PROFILE_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.SCORED_INTERPRETATION,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["YSQ-L3", "Young Schema Questionnaire", "early maladaptive schemas", "schema therapy assessment"],
		defaultConfig: createYoungSchemaQuestionnaireL3Config(),
		publicDemoConfig: {instructions: "Rate the statements and review the schema profile.", demoEnginePayload: createYoungSchemaQuestionnaireL3DemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.INTERPERSONAL_INVENTORY,
		slug: "interpersonal-inventory",
		title: "Interpersonal Inventory",
		shortDescription: "Map significant relationships, support, strain, and presenting-problem context during IPT assessment.",
		displaySummary: "Record important current or past relationships and review their support, strain, stressors, and possible links to the presenting problem.",
		interactionModel: "The practitioner records relationships during assessment and adds formulation notes after reviewing the inventory.",
		engine: ToolEngineEnum.STAGED_WORKSHEET,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.RELATIONSHIP_INVENTORY_CONFIG,
		workRenderer: ToolRendererEnum.RELATIONSHIP_INVENTORY_WORK,
		resultRenderer: ToolRendererEnum.RELATIONSHIP_INVENTORY_RESULT,
		blankPrintRenderer: ToolRendererEnum.RELATIONSHIP_INVENTORY_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.RELATIONSHIP_INVENTORY_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["IPT", "interpersonal psychotherapy", "interpersonal inventory", "social support", "relationship map"],
		defaultConfig: createInterpersonalInventoryConfig(),
		publicDemoConfig: {demoEnginePayload: createInterpersonalInventoryDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.PRESENT_TRIGGER_WORKSHEET,
		slug: "present-trigger-worksheet",
		title: "Present Trigger Worksheet",
		shortDescription: "Capture one current EMDR trigger and its associated responses for practitioner review.",
		displaySummary: "Describe one present triggering situation, record the related responses, and review its use as a treatment target.",
		interactionModel: "Practitioner and client complete one present-trigger worksheet together; the practitioner reviews target usability privately.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.FORM_TARGET_CONFIG,
		workRenderer: ToolRendererEnum.FORM_TARGET_WORK,
		resultRenderer: ToolRendererEnum.FORM_TARGET_RESULT,
		blankPrintRenderer: ToolRendererEnum.FORM_TARGET_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.FORM_TARGET_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["EMDR", "present trigger", "target definition", "current trigger", "VoC", "SUD"],
		defaultConfig: createPresentTriggerWorksheetConfig(),
		publicDemoConfig: {demoEnginePayload: {answers: {triggerList: "Crowded train at rush hour; my supervisor raising her voice", selectedTarget: "A crowded train stopping between stations", picture: "People pressing close while the doors stay shut", negativeCognition: "I cannot get out", positiveCognition: "I can stay grounded until the train moves", voc: 4, emotions: "Fear and helplessness", sud: 6, bodySensation: "Tightness in my chest"}}},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.IMAGINAL_EXPOSURE_SCRIPT_WORKSHEET,
		slug: "imaginal-exposure-script-worksheet",
		title: "Imaginal Exposure Script Worksheet",
		shortDescription: "Create a repeatable feared-outcome script with explicit response-prevention rules.",
		displaySummary: "Draft a first-person feared-outcome narrative and review its readiness for repeated OCD ERP practice.",
		interactionModel: "Practitioner and client construct the script together, then review the target, delivery mode, and response-prevention rules.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.SCRIPT_WORKSHEET_CONFIG,
		workRenderer: ToolRendererEnum.SCRIPT_WORKSHEET_WORK,
		resultRenderer: ToolRendererEnum.SCRIPT_WORKSHEET_RESULT,
		blankPrintRenderer: ToolRendererEnum.SCRIPT_WORKSHEET_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.SCRIPT_WORKSHEET_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["OCD", "ERP", "imaginal exposure", "feared consequence", "ritual prevention"],
		defaultConfig: createImaginalExposureScriptConfig(),
		publicDemoConfig: {demoEnginePayload: createImaginalExposureScriptDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.PATIENT_RULES_FOR_RITUAL_PREVENTION,
		slug: "patient-rules-for-ritual-prevention",
		title: "Patient Rules for Ritual Prevention",
		shortDescription: "Share editable ritual-prevention rules for exposure practice.",
		displaySummary: "Prepare a focused patient handout with practitioner-authored rules, context, optional acknowledgement, and review notes.",
		interactionModel: "The practitioner configures and marks a rule handout ready. The patient reads it, acknowledges review if requested, and may send questions.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.RITUAL_HANDOUT_CONFIG,
		workRenderer: ToolRendererEnum.RITUAL_HANDOUT_WORK,
		resultRenderer: ToolRendererEnum.RITUAL_HANDOUT_RESULT,
		blankPrintRenderer: ToolRendererEnum.RITUAL_HANDOUT_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.RITUAL_HANDOUT_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["OCD", "ERP", "EX/RP", "washing", "checking", "ritual prevention"],
		defaultConfig: createPatientRulesForRitualPreventionConfig(),
		publicDemoConfig: {config: createPatientRulesForRitualPreventionDemoConfig(), demoOnlyConfigPaths: ["handoutConfig.context"], demoEnginePayload: createPatientRulesForRitualPreventionDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.IMPACT_STATEMENT,
		slug: "impact-statement",
		title: "Impact Statement for Cognitive Processing Therapy (CPT)",
		shortDescription: "A reflective assignment about current trauma-related beliefs.",
		displaySummary: "Collect a written baseline, review practitioner-identified themes, and revisit the original statement later.",
		interactionModel: "The client writes a statement, saves a draft, and submits it for practitioner review.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.REFLECTIVE_STATEMENT_CONFIG,
		workRenderer: ToolRendererEnum.REFLECTIVE_STATEMENT_WORK,
		resultRenderer: ToolRendererEnum.REFLECTIVE_STATEMENT_RESULT,
		blankPrintRenderer: ToolRendererEnum.REFLECTIVE_STATEMENT_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.REFLECTIVE_STATEMENT_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["CPT Impact Statement", "trauma beliefs", "Cognitive Processing Therapy"],
		defaultConfig: createImpactStatementConfig(),
		publicDemoConfig: {demoEnginePayload: createImpactStatementDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.STATEMENT_OF_POSITION_MAP,
		slug: "statement-of-position-map",
		title: "Statement of Position Map",
		shortDescription: "A narrative therapy map for clarifying a client's stance toward a problem.",
		displaySummary: "Name a problem, explore its effects, evaluate those effects, and explain the reasons for that position.",
		interactionModel: "The client and practitioner work through four inquiry areas using the client's own words.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.POSITION_MAP_CONFIG,
		workRenderer: ToolRendererEnum.POSITION_MAP_WORK,
		resultRenderer: ToolRendererEnum.POSITION_MAP_RESULT,
		blankPrintRenderer: ToolRendererEnum.POSITION_MAP_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.POSITION_MAP_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["narrative therapy", "externalising", "statement of position", "problem effects", "evaluation"],
		defaultConfig: createStatementOfPositionMapConfig(),
		publicDemoConfig: {demoEnginePayload: {answers: {
			problemName: "The rush to agree",
			problemEffects: "I cancel plans with friends to avoid disappointing colleagues.",
			effectsEvaluation: "It helps at work sometimes, but I dislike losing time with friends.",
		}}},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.OCI_CV,
		slug: "obsessive-compulsive-inventory-child-version-oci-cv",
		title: "Obsessive-Compulsive Inventory – Child Version (OCI-CV)",
		shortDescription: "Youth self-report of obsessive-compulsive symptoms during the past month.",
		displaySummary: "Collect youth ratings and review a descriptive total and six symptom domains.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.FREQUENCY_INVENTORY_CONFIG,
		workRenderer: ToolRendererEnum.FREQUENCY_INVENTORY_WORK,
		resultRenderer: ToolRendererEnum.FREQUENCY_INVENTORY_RESULT,
		blankPrintRenderer: ToolRendererEnum.FREQUENCY_INVENTORY_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.FREQUENCY_INVENTORY_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.SCORED_INTERPRETATION,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["OCI-CV", "child OCD self-report", "pediatric obsessive-compulsive symptoms"],
		defaultConfig: createOciCvConfig(),
		publicDemoConfig: {instructions: "Rate each statement and review the youth-reported profile.", demoEnginePayload: createOciCvDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.IPT_TIMELINE,
		slug: "ipt-timeline",
		title: "IPT Timeline",
		shortDescription: "Place interpersonal events, transitions, and symptom changes in chronological order.",
		displaySummary: "Review reported timing and record a practitioner-authored interpersonal hypothesis.",
		interactionModel: "Client and practitioner reconstruct and refine the chronology together.",
		engine: ToolEngineEnum.TIMELINE,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.IPT_TIMELINE_CONFIG,
		workRenderer: ToolRendererEnum.TIMELINE_CANVAS_WORK,
		resultRenderer: ToolRendererEnum.TIMELINE_CHRONOLOGY_RESULT,
		blankPrintRenderer: ToolRendererEnum.TIMELINE_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.TIMELINE_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.ORDERED_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["interpersonal psychotherapy", "IPT", "symptom chronology", "role transition"],
		defaultConfig: {
			title: "IPT Timeline",
			instructions: "Place relevant interpersonal events, role changes, and symptom changes in order. Approximate timing is welcome.",
			reviewInstructions: "Review the sequence and reported timing. Timing alone does not establish cause.",
			entryPrompt: "What happened or changed?",
			notesPrompt: "What context would help explain this entry?",
			hypothesisPrompt: "What interpersonal sequence seems most relevant to the current treatment focus?",
			showPriorResults: true,
			categoryQuestion: "Entry category",
			timingQuestion: "Reported timing",
			exactDateQuestion: "Date",
			approximateQuestion: "Approximate period",
			relativeQuestion: "Relative timing",
			interpretationTitle: "Practitioner interpretation",
			uncertaintyQuestion: "Is the timing or account uncertain?",
			uncertaintyLabel: "Timing or account uncertain",
			categories: [{id: "interpersonal", title: "Interpersonal event", kind: "event"}, {id: "transition", title: "Role transition", kind: "transition"}, {id: "symptom", title: "Symptom change", kind: "symptom"}],
			timingOptions: [{id: "exact", title: "Exact date", kind: "exact"}, {id: "approximate", title: "Approximate period", kind: "approximate"}, {id: "relative", title: "Relative order", kind: "relative"}],
		},
		publicDemoConfig: {demoEnginePayload: {timeline: {events: [
			{id: "move", label: "Moved to a new city", categoryId: "transition", timingOptionId: "exact", exactDate: "2025-03-01", exactDateFormat: "MMM YYYY", notes: "Leaving friends nearby felt harder than expected.", order: 0},
			{id: "contact", label: "Weekly calls with my sister became less frequent", categoryId: "interpersonal", timingOptionId: "", notes: "After the move", order: 1},
			{id: "sleep", label: "Sleep became more disrupted", categoryId: "symptom", timingOptionId: "exact", exactDate: "2025-05-12", order: 2},
		]}}},
	}),
	gradedTaskAssignmentDefinition,
	cciGradedTasksAndExposureDefinition,
	new ToolDefinition({
		toolType: ToolTypeEnum.TIMELINE,
		slug: "timeline",
		title: "Family Planning Timeline",
		shortDescription: "Build a shared chronology of family, case, and relationship events.",
		displaySummary: "Place major events in order, preserve uncertain timing, and prepare follow-up conversations.",
		interactionModel: "Practitioners and family participants add, correct, and discuss events in one chronology.",
		engine: ToolEngineEnum.TIMELINE,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.TIMELINE_DETAIL_CONFIG,
		workRenderer: ToolRendererEnum.TIMELINE_DETAIL_WORK,
		resultRenderer: ToolRendererEnum.TIMELINE_DETAIL_RESULT,
		blankPrintRenderer: ToolRendererEnum.TIMELINE_DETAIL_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.TIMELINE_DETAIL_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.ORDERED_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["Lifelong Links", "family-led planning", "chronology", "family history"],
		defaultConfig: {
			title: "Family Planning Timeline",
			showPriorResults: true,
			instructions: "Add events that shaped the child's family and support network. Use an approximate period when an exact date is unknown. Note corrections and questions to follow up.",
			reviewInstructions: "Read the events in order. Discuss uncertain accounts and follow-up needs without assuming why an event happened.",
			categories: [
				{id: "placement", title: "Placements"}, {id: "separation", title: "Separations"},
				{id: "contact", title: "Contacts"}, {id: "relationship", title: "Relationship changes"},
			],
			fields: [
				{id: "description", title: "Event or transition", type: "text"},
				{id: "exactDate", title: "Exact date", type: "date"},
				{id: "approximatePeriod", title: "Approximate year or period", type: "text"},
				{id: "people", title: "People involved", type: "text"},
				{id: "change", title: "What changed?", type: "text"},
				{id: "relevance", title: "Relevance to current planning", type: "text"},
				{id: "uncertainty", title: "Uncertainty or differing accounts", type: "text"},
				{id: "sourceContext", title: "Source or context", type: "text"},
				{id: "followUp", title: "Follow-up needed", type: "text"},
			],
		},
		publicDemoConfig: {demoEnginePayload: {timeline: {events: [
			{id: "move", categoryId: "placement", categoryTitle: "Placements", exactDate: "2021-04-12", description: "Moved to Aunt Maria's home", people: "Maria and Noah", relevance: "Maria remains a regular support", order: 0},
			{id: "visit", categoryId: "contact", categoryTitle: "Contacts", approximatePeriod: "2022", description: "Weekend visits with Grandad resumed", relevance: "Grandad can join planning", order: 1},
			{id: "school", categoryId: "relationship", categoryTitle: "Relationship changes", exactDate: "2023-09-01", description: "Reconnected with cousin at school", relevance: "They now see each other weekly", order: 2},
		]}}},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.COIS_R,
		slug: "cois-r-pediatric-ocd-impairment",
		title: "Child Obsessive-Compulsive Impact Scale-Revised (COIS-R)",
		shortDescription: "Parent and child reports of OCD-related functional impairment.",
		displaySummary: "Collect separate parent and child ratings of trouble caused by OCD in the past month.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.INDEPENDENT_MULTI,
		participationPolicy: {roles: [
			{id: "parent", title: "Parent form", capabilities: {canWork: true, canViewResult: false}, allowsMultipleAssignments: true},
			{id: "child", title: "Child form", capabilities: {canWork: true, canViewResult: false}, allowsMultipleAssignments: true},
		]},
		configRenderer: ToolRendererEnum.COIS_R_CONFIG,
		workRenderer: ToolRendererEnum.COIS_R_WORK,
		resultRenderer: ToolRendererEnum.COIS_R_RESULT,
		blankPrintRenderer: ToolRendererEnum.COIS_R_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.COIS_R_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.TREND_REVIEW,
		longitudinalClassId: LongitudinalClassId.SCORED_FORM,
		longitudinalRendererId: LongitudinalRendererId.SCORED_FORM,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["COIS-R", "pediatric OCD", "functional impairment", "parent report", "child report"],
		defaultConfig: createCoisRConfig(),
		publicDemoConfig: {instructions: "Explore the ratings and review the results.", demoEnginePayload: createCoisRDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.CY_BOCS,
		slug: "children-s-yale-brown-obsessive-compulsive-scale-cy-bocs",
		title: "Children's Yale-Brown Obsessive Compulsive Scale (CY-BOCS)",
		shortDescription: "Clinician-rated pediatric OCD symptom checklist and severity assessment.",
		displaySummary: "Interview child and parent or guardian, review current and past symptoms, and rate ten severity items.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.CLINICIAN_ASSESSMENT_CONFIG,
		workRenderer: ToolRendererEnum.CLINICIAN_ASSESSMENT_WORK,
		resultRenderer: ToolRendererEnum.CLINICIAN_ASSESSMENT_RESULT,
		blankPrintRenderer: ToolRendererEnum.CLINICIAN_ASSESSMENT_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.CLINICIAN_ASSESSMENT_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.SCORED_INTERPRETATION,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["CY-BOCS", "pediatric OCD", "obsessions", "compulsions"],
		defaultConfig: createCyBocsConfig(),
		publicDemoConfig: {instructions: "Enter clinician ratings and review the results.", demoEnginePayload: createCyBocsDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.CY_BOCS_II,
		slug: "cy-bocs-ii-pediatric-ocd-assessment",
		title: "Children's Yale-Brown Obsessive-Compulsive Scale Second Edition (CY-BOCS-II)",
		shortDescription: "Revised clinician-rated pediatric OCD symptom and severity assessment.",
		displaySummary: "Interview child and caregiver, review the revised 66-symptom checklist, and rate ten severity items from 0 to 5.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.CLINICIAN_ASSESSMENT_CONFIG,
		workRenderer: ToolRendererEnum.CLINICIAN_ASSESSMENT_WORK,
		resultRenderer: ToolRendererEnum.CLINICIAN_ASSESSMENT_RESULT,
		blankPrintRenderer: ToolRendererEnum.CLINICIAN_ASSESSMENT_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.CLINICIAN_ASSESSMENT_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.SCORED_INTERPRETATION,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["CY-BOCS-II", "pediatric OCD", "obsessions", "compulsions"],
		defaultConfig: createCyBocsSecondEditionConfig(),
		publicDemoConfig: {instructions: "Enter clinician ratings and review the results.", demoEnginePayload: createCyBocsSecondEditionDemoPayload()},
	}),
	new ToolDefinition({
		toolType: ToolTypeEnum.CAREER_CONSTRUCTION_INTERVIEW,
		slug: "career-construction-interview-cci",
		title: "Career Construction Interview (CCI)",
		shortDescription: "A narrative career counseling interview that connects client stories with practitioner-confirmed themes.",
		displaySummary: "Gather a presenting concern, five interview topics, and three early recollections; review source-linked themes and a life-career portrait.",
		interactionModel: "The client and counselor move through one narrative prompt at a time. The counselor then writes a source-grounded formulation.",
		engine: ToolEngineEnum.FORM,
		participationMode: ToolParticipationModeEnum.SINGLE,
		configRenderer: ToolRendererEnum.NARRATIVE_INTERVIEW_CONFIG,
		workRenderer: ToolRendererEnum.NARRATIVE_INTERVIEW_WORK,
		resultRenderer: ToolRendererEnum.NARRATIVE_INTERVIEW_RESULT,
		blankPrintRenderer: ToolRendererEnum.NARRATIVE_INTERVIEW_BLANK_PRINT,
		resultPrintRenderer: ToolRendererEnum.NARRATIVE_INTERVIEW_RESULT_PRINT,
		resultBehavior: ToolResultBehaviorEnum.WORKSHEET_REVIEW,
		supportedSurfaces: sharedFormSurfaces,
		searchKeywords: ["CCI", "career construction counseling", "narrative career interview", "life-career portrait"],
		defaultConfig: createCareerConstructionInterviewConfig(),
		publicDemoConfig: {demoEnginePayload: createCareerConstructionInterviewDemoPayload()},
	}),
	readinessRulerDefinition,
	importanceRulerDefinition,
	wallQuestionDefinition,
	wheelOfLifeDefinition,
	happinessScaleDefinition,
	valuesBullseyeDefinition,
	moodDiaryDefinition,
	selfMonitoringFormDefinition,
	momentaryTimeSamplingDefinition,
	subjectiveUnitsOfDistressScaleSudsDefinition,
	dbtSkillsDiaryCardDefinition,
	nidaDiaryCardDefinition,
	valuesCardSortDefinition,
	dbtChainAnalysisDefinition,
	dbtSolutionAnalysisDefinition,
	systematicEvocativeUnfoldingDefinition,
	missingLinksAnalysisDefinition,
	linehanRiskAssessmentAndManagementProtocolLrampDefinition,
	lifetimeSuicideAttemptSelfInjuryCountLSasiDefinition,
	imminentRiskAndActionPlanDefinition,
	patternsOfProblematicThinkingDefinition,
]
const hiddenCatalogToolTypes = new Set([
	ToolTypeEnum.WHEEL_OF_LIFE,
])

export default class ToolDefinitionCatalog {
	constructor() {
		this.toolDefinitionsArray = toolDefinitionsArray
		this.catalogToolDefinitionsArray = this.toolDefinitionsArray.filter(definition => !hiddenCatalogToolTypes.has(definition.toolType) && isToolAvailable(definition.toolType))
		this.toolDefinitionsByType = this.createDefinitionsMap("toolType")
		this.toolDefinitionsBySlug = this.createDefinitionsMap("slug")
	}

	createDefinitionsMap(fieldName) {
		return this.toolDefinitionsArray.reduce((result, definition) => {
			result[definition[fieldName]] = definition
			return result
		}, {})
	}

	getToolDefinitions() {
		return this.catalogToolDefinitionsArray
	}

	getToolDefinitionByType(toolType) {
		return isToolAvailable(toolType) ? this.toolDefinitionsByType[toolType] || null : null
	}

	getToolDefinitionBySlug(slug) {
		const definition = this.toolDefinitionsBySlug[slug]
		return definition && isToolAvailable(definition.toolType) ? definition : null
	}

	searchToolDefinitions(searchText = "") {
		return this.catalogToolDefinitionsArray.filter(definition => definition.matchesSearch(searchText))
	}

	isValidToolType(toolType) {
		return !!this.getToolDefinitionByType(toolType)
	}
}
